<template>
    <div class="relative flex flex-col h-full min-w-0"
        @dragenter="onFileDragEnter" @dragover="onFileDragOver"
        @dragleave="onFileDragLeave" @drop="onFileDrop" @dragend="fileDragDepth = 0">
        <div v-if="fileDragDepth > 0 && !chatInputDisabled"
            class="pointer-events-none absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-400 bg-blue-50/95 text-blue-700"
            role="status">
            <UIcon name="i-heroicons-paper-clip" class="h-8 w-8" />
            <span class="text-base font-medium">Drop files to attach</span>
            <span class="text-sm">Up to 10 MB per file · Added to your draft</span>
        </div>
        <div v-if="otherActiveChats.length > 0" class="flex-none border-b border-amber-200 bg-amber-50 px-4 py-3">
            <div class="mx-auto max-w-3xl">
                <div class="flex items-start justify-between gap-3">
                    <button type="button" class="flex min-w-0 items-start gap-2 text-left" @click="showOtherActiveChats = !showOtherActiveChats">
                        <UIcon name="i-heroicons-exclamation-triangle" class="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                        <div class="min-w-0">
                            <div class="text-sm font-medium text-amber-900">
                                {{ otherActiveChats.length }} other Coldreach AI {{ otherActiveChats.length === 1 ? 'chat is' : 'chats are' }} working on this campaign
                            </div>
                            <div class="text-xs text-amber-700">Review or stop them to avoid conflicting campaign changes.</div>
                        </div>
                        <UIcon :name="showOtherActiveChats ? 'i-heroicons-chevron-up' : 'i-heroicons-chevron-down'" class="mt-1 h-3.5 w-3.5 flex-none text-amber-600" />
                    </button>
                    <button type="button" class="flex-none text-xs font-medium text-amber-800 hover:text-amber-950 disabled:opacity-50" :disabled="stoppingOtherChatIds.length > 0" @click="stopAllOtherActiveChats">
                        Stop all others
                    </button>
                </div>
                <div v-if="showOtherActiveChats" class="mt-3 space-y-2 border-t border-amber-200 pt-3">
                    <div v-for="otherChat in otherActiveChats" :key="otherChat.id" class="flex items-center gap-3 rounded-md bg-white/70 px-3 py-2 ring-1 ring-amber-200">
                        <NuxtLink :to="otherChatLink(otherChat.id)" class="min-w-0 flex-1 hover:text-blue-700">
                            <div class="truncate text-sm font-medium text-slate-800">{{ otherChat.title || 'Coldreach AI chat' }}</div>
                            <div class="truncate text-xs text-slate-500">{{ otherChat.ownerLabel || otherChat.ownerName || 'Team member' }} · updated {{ formatAsRelativeDate(otherChat.updatedAt) }}</div>
                        </NuxtLink>
                        <button type="button" class="flex-none text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50" :disabled="stoppingOtherChatIds.includes(otherChat.id)" @click="stopOtherChat(otherChat.id)">
                            {{ stoppingOtherChatIds.includes(otherChat.id) ? 'Stopping…' : 'Stop' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <CampaignEditChatAccountEvidenceContext v-if="props.context?.page === 'contact-review' && props.context.contactReview && icpId"
            :campaign-id="icpId" :assignment-id="props.context.contactReview.assignmentId" />

        <!-- Messages Container -->
        <div class="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-3 chat-bg" ref="messagesContainer" @scroll="onMessagesScroll">
            <!-- Loading state while fetching chat history -->
            <div v-if="loadingChat" class="flex flex-col items-center justify-center h-full min-h-[400px]">
                <div class="flex items-center gap-2 text-slate-400">
                    <UIcon name="i-heroicons-arrow-path" class="w-5 h-5 animate-spin" />
                    <span class="text-sm">Loading conversation…</span>
                </div>
            </div>

            <div v-else-if="chat.messages.length === 0" class="flex flex-col items-center justify-center h-full min-h-[400px]">
                <!-- New Campaign: guided onboarding empty state -->
                <div v-if="isNewCampaignFlow" class="w-full max-w-lg space-y-5">
                    <div class="text-center">
                        <h2 class="text-base font-semibold text-slate-800">Create a Campaign</h2>
                        <p class="text-sm text-slate-500 mt-1">Tell me who you want to reach and I'll build targeting, research signals, and email sequences.</p>
                    </div>

                    <!-- Fill-in-the-blank prompt starters -->
                    <div class="space-y-2">
                        <p class="text-xs font-medium text-slate-400 uppercase tracking-wide ml-1">Start with one of these</p>
                        <button v-for="starter in newCampaignStarters" :key="starter.template" @click="prefillPrompt(starter.template, starter.cursorOffset)" class="w-full text-left px-3 py-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all group">
                            <span class="text-sm text-slate-700 group-hover:text-slate-900">
                                <span v-for="(seg, i) in parseTemplate(starter.template)" :key="i">
                                    <span v-if="seg.placeholder" class="text-blue-500 font-medium border-b border-blue-300 border-dashed">{{ seg.text }}</span>
                                    <span v-else>{{ seg.text }}</span>
                                </span>
                            </span>
                        </button>
                    </div>

                    <!-- Hint about what the AI will ask -->
                    <p class="text-xs text-slate-400 text-center">I'll help you define: <span class="text-slate-500 font-medium">Who to target</span> · <span class="text-slate-500 font-medium">What you sell</span> · <span class="text-slate-500 font-medium">Why it's relevant now</span></p>
                </div>

                <!-- Campaign editor: AI capabilities overview -->
                <div v-else-if="isCampaignEditorFlow" class="w-full max-w-lg space-y-5">
                    <div v-for="group in campaignEditorGroups" :key="group.title" class="space-y-1.5">
                        <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wide ml-1">{{ group.title }}</p>
                        <button v-for="action in group.actions" :key="action.label" @click="sendExample(action.prompt)" class="w-full text-left flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all group/action">
                            <UIcon :name="action.icon" class="w-4 h-4 text-slate-400 group-hover/action:text-blue-500 flex-shrink-0 mt-0.5 transition-colors" />
                            <div class="min-w-0">
                                <div class="text-sm font-medium text-slate-700 group-hover/action:text-slate-900">{{ action.label }}</div>
                                <div class="text-xs text-slate-400 mt-0.5">{{ action.description }}</div>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Other flows: existing example prompts -->
                <div v-else class="w-full max-w-lg space-y-2">
                    <p class="text-xs font-medium text-slate-500 mb-2 ml-1">Try one of these:</p>
                    <button v-for="example in examplePrompts" :key="example" @click="sendExample(example)" class="w-full text-left px-2.5 py-2.5 rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-sm text-slate-700 hover:text-slate-900">
                        <span class="opacity-75">{{ example }}</span>
                    </button>
                </div>
            </div>

            <!-- Load earlier messages -->
            <div v-if="hasEarlierMessages" class="flex justify-center py-2">
                <button type="button" @click="loadEarlierMessages" class="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors">
                    Load {{ Math.min(MESSAGES_PER_PAGE, hiddenMessageCount) }} earlier messages ({{ hiddenMessageCount }} hidden)
                </button>
            </div>
            <div v-for="(m, index) in visibleMessages" :key="m.id || index" class="w-full" :class="m.role === 'user' ? 'mt-6' : index > 0 ? 'mt-1' : ''">
                <!-- User message — right-aligned bubble -->
                <template v-if="m.role === 'user'">
                    <div v-for="(group, groupIndex) in groupParts(m.parts)" :key="`${m.id || index}-${groupIndex}`">
                        <div v-if="group.type === 'text'" class="flex w-full justify-end">
                            <div class="flex min-w-0 max-w-[70%] flex-col items-end gap-1">
                                <div v-if="getSenderLabel(m)" class="px-1 text-[11px] font-medium leading-none text-slate-400">
                                    {{ getSenderLabel(m) }}
                                </div>
                                <div class="min-w-0 w-full rounded-lg px-4 py-2.5 bg-blue-50 text-slate-800 ring-1 ring-blue-100">
                                    <div v-for="(part, partIndex) in group.parts" :key="partIndex" class="min-w-0">
                                        <div v-if="part.type === 'text'" class="chat-markdown text-sm" v-html="md.render(part.text || '')"></div>
                                        <div v-else-if="part.type === 'file' && asFilePart(part).mediaType?.startsWith('image/')" class="mt-1">
                                            <img :src="asFilePart(part).url" class="max-w-full max-h-48 rounded" :alt="asFilePart(part).filename || 'Uploaded image'" />
                                        </div>
                                        <div v-else-if="part.type === 'file'" class="mt-1 flex items-center gap-1.5 text-xs text-blue-600">
                                            <UIcon name="i-heroicons-document" class="w-4 h-4 flex-shrink-0" />
                                            <span class="truncate">{{ asFilePart(part).filename || "Uploaded file" }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- AI message — white card on tinted background -->
                <template v-else>
                    <div class="space-y-1.5 bg-white rounded-lg px-4 py-3 shadow-sm ring-1 ring-slate-100">
                        <div v-if="getModelId(m) && !getModelId(m)!.startsWith('claude')" class="flex items-center gap-1 text-xs text-amber-600">
                            <UIcon name="i-heroicons-arrow-path" class="w-3 h-3" />
                            <span>Using fallback model ({{ getModelId(m) }})</span>
                        </div>
                        <template v-for="(group, groupIndex) in groupPartsCollapsed(m.parts)" :key="`${m.id || index}-${groupIndex}`">
                            <!-- Text/Reasoning parts -->
                            <div v-if="group.type === 'text'" class="w-full">
                                <div v-for="(part, partIndex) in group.parts" :key="partIndex" class="min-w-0">
                                    <template v-if="part.type === 'text'">
                                        <!-- Completed thinking blocks — collapsed -->
                                        <details v-for="(block, bi) in extractThinking(part.text || '').thinkingBlocks" :key="`think-${bi}`" class="collapsible-block mb-2 text-xs">
                                            <summary class="cursor-pointer select-none py-0.5 text-slate-500 hover:text-slate-700 font-medium flex items-center gap-2">
                                                <UIcon name="i-heroicons-chevron-right" class="w-3 h-3 flex-shrink-0 collapsible-chevron" />
                                                <UIcon name="i-heroicons-light-bulb" class="w-3.5 h-3.5 flex-shrink-0" />
                                                Thinking
                                            </summary>
                                            <div class="collapsible-content ml-1.5 pl-4 border-l border-slate-200 chat-markdown pb-2 pt-0.5 text-slate-500 italic leading-relaxed" v-html="md.render(block)"></div>
                                        </details>
                                        <!-- Pending (unclosed) thinking block — open, last 3 lines -->
                                        <details v-if="extractThinking(part.text || '').pendingThinking" open class="collapsible-block mb-2 text-xs">
                                            <summary class="cursor-pointer select-none py-0.5 text-blue-500 hover:text-blue-700 font-medium flex items-center gap-2">
                                                <UIcon name="i-heroicons-chevron-right" class="w-3 h-3 flex-shrink-0 collapsible-chevron" />
                                                <span class="thinking-dot" />
                                                Thinking…
                                            </summary>
                                            <div class="collapsible-content ml-1.5 pl-4 border-l border-blue-200 chat-markdown pb-2 pt-0.5 text-slate-500 italic leading-relaxed" v-html="md.render(lastNLines(extractThinking(part.text || '').pendingThinking!, 3))"></div>
                                        </details>
                                        <div v-if="extractThinking(part.text || '').cleaned" class="chat-markdown text-sm text-slate-800" v-html="md.render(extractThinking(part.text || '').cleaned)"></div>
                                    </template>
                                    <!-- Provider-designated progress stays visible; empty signed blocks stay hidden. -->
                                    <div v-else-if="getChatReasoningDisplay(part, getModelId(m)) === 'progress'"
                                        class="chat-markdown text-sm text-slate-800" data-chat-progress
                                        v-html="md.render(part.text || '')"
                                    ></div>
                                    <!-- Other reasoning retains its existing collapsible presentation. -->
                                    <details v-else-if="getChatReasoningDisplay(part, getModelId(m)) === 'reasoning'" :open="isActiveReasoning(index, partIndex, group.parts) ? true : undefined" class="collapsible-block mb-2 text-xs">
                                        <summary class="cursor-pointer select-none py-0.5 font-medium flex items-center gap-2" :class="isActiveReasoning(index, partIndex, group.parts) ? 'text-blue-500 hover:text-blue-700' : 'text-slate-500 hover:text-slate-700'">
                                            <UIcon name="i-heroicons-chevron-right" class="w-3 h-3 flex-shrink-0 collapsible-chevron" />
                                            <template v-if="isActiveReasoning(index, partIndex, group.parts)">
                                                <span class="thinking-dot" />
                                                Thinking…
                                            </template>
                                            <template v-else>
                                                <UIcon name="i-heroicons-light-bulb" class="w-3.5 h-3.5 flex-shrink-0" />
                                                Thinking
                                            </template>
                                        </summary>
                                        <div
                                            class="collapsible-content ml-1.5 pl-4 border-l chat-markdown pb-2 pt-0.5 text-slate-500 italic leading-relaxed"
                                            :class="isActiveReasoning(index, partIndex, group.parts) ? 'border-blue-200' : 'border-slate-200'"
                                            v-html="md.render(isActiveReasoning(index, partIndex, group.parts) ? lastNLines(part.text || '', 3) : part.text || '')"
                                        ></div>
                                    </details>
                                    <div v-else-if="part.type === 'file' && asFilePart(part).mediaType?.startsWith('image/')" class="mt-1">
                                        <img :src="asFilePart(part).url" class="max-w-full max-h-48 rounded" :alt="asFilePart(part).filename || 'Uploaded image'" />
                                    </div>
                                    <div v-else-if="part.type === 'file'" class="mt-1 flex items-center gap-1.5 text-xs text-blue-600">
                                        <UIcon name="i-heroicons-document" class="w-4 h-4 flex-shrink-0" />
                                        <span class="truncate">{{ asFilePart(part).filename || "Uploaded file" }}</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Tool calls — each rendered individually -->
                            <template v-else-if="group.type === 'tool'">
                                <template v-for="(part, pi) in group.parts" :key="getPartKey(part as ToolPart, pi)">
                                    <CampaignEditChatExportDownload v-if="part.type === 'tool-showExportDownload'" :part="part" />
                                    <details v-else-if="isToolInputValidationError(part as ToolPart)" class="collapsible-block my-2 min-w-0 text-xs text-slate-500">
                                        <summary class="flex cursor-pointer select-none items-center gap-2 py-1 hover:text-slate-700">
                                            <UIcon name="i-heroicons-chevron-right" class="h-3 w-3 flex-shrink-0 collapsible-chevron" />
                                            <span>Tool input needs adjustment</span>
                                        </summary>
                                        <pre class="m-0 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md bg-slate-50 p-3 text-xs">{{ (part as ToolPart).errorText }}</pre>
                                    </details>
                                    <CampaignEditChatCampaignPlan
                                        v-else-if="part.type === 'tool-showCampaignPlan'"
                                        :part="part"
                                        :input="part.input"
                                        :active="(isLoading || streamingChat) && !interruptedExecutionId && !stoppedUserMessage && !chat.error && !recoveryError && m.id === chat.messages.at(-1)?.id"
                                    />
                                    <CampaignEditChatCampaignModificationPlan v-else-if="part.type === 'tool-showCampaignModificationPlan'" :part="part" />
                                    <CampaignEditChatCreativeBets
                                        v-else-if="part.type === 'tool-showCreativeBets'"
                                        :part="part"
                                        @draft-bet="handleCreativeBetDraft"
                                        @give-feedback="handleCreativeBetFeedback"
                                        @draft-step1-ab-test="handleCreativeBetStep1AbTestDraft"
                                        @request-variation="handleCreativeBetVariation"
                                    />
                                    <CampaignEditChatCreativeBetFollowupArc v-else-if="part.type === 'tool-showCreativeBetFollowupArc'" :part="part" @draft-edits="handleCreativeBetFollowupArcDraftEdits" />
                                    <div v-else-if="part.type === 'tool-launchCampaign' && isSuccessfulLaunchPart(part as ToolPart)" class="space-y-2">
                                        <CampaignEditChatToolBlock :part="part" :metadata="m.metadata" :active="(isLoading || streamingChat) && !interruptedExecutionId && m.id === chat.messages.at(-1)?.id" />
                                        <UButton v-if="showPostLaunchAction" color="primary" variant="solid" size="sm" icon="i-heroicons-arrow-right" trailing class="w-full justify-center" @click="openPostLaunchCampaign">
                                            {{ postLaunchActionLabel }}
                                        </UButton>
                                        <CampaignEditChatAIScoreCard v-if="isGlobalAdmin" :campaign-id="icpId!" :iteration-created="isIterationLaunchPart(part as ToolPart)" :launch-work-session-id="launchWorkSessionId(part as ToolPart)" @improve="handleAIScoreImprove" />
                                    </div>
                                    <CampaignEditChatCampaignEligibilityPolicy
                                        v-else-if="part.type === 'tool-proposeCampaignEligibilityPolicy'"
                                        :part="part as ToolPart"
                                        :can-confirm="canConfirmEligibilityPolicy(m.id, part as ToolPart)"
                                        :saving="eligibilityPolicyConfirmationState[eligibilityPolicyConfirmationKey(m.id, part as ToolPart)]?.saving"
                                        :error="eligibilityPolicyConfirmationState[eligibilityPolicyConfirmationKey(m.id, part as ToolPart)]?.error"
                                        @confirm="confirmEligibilityPolicy(m.id, part as ToolPart)"
                                        @reload="reloadEligibilityPolicy(m.id, part as ToolPart)"
                                    />
                                    <CampaignEditChatProductContextProposal
                                        v-else-if="part.type === 'tool-proposeProductContext'"
                                        :part="part as ToolPart"
                                        :can-confirm="canConfirmProductContextProposal(part as ToolPart)"
                                        :saving="productContextConfirmationState[productContextConfirmationKey(m.id, part as ToolPart)]?.saving"
                                        :error="productContextConfirmationState[productContextConfirmationKey(m.id, part as ToolPart)]?.error"
                                        @confirm="confirmProductContextProposal(m.id, part as ToolPart)"
                                    />
                                    <CampaignEditChatWritingStyleProposal
                                        v-else-if="part.type === 'tool-proposeWritingStyleChange'"
                                        :part="part as ToolPart"
                                        :can-confirm="canConfirmWritingStyleProposal(part as ToolPart)"
                                        :saving="writingStyleConfirmationState[writingStyleConfirmationKey(m.id, part as ToolPart)]?.saving"
                                        :error="writingStyleConfirmationState[writingStyleConfirmationKey(m.id, part as ToolPart)]?.error"
                                        @confirm="confirmWritingStyleProposal(m.id, part as ToolPart, $event)"
                                    />
                                    <CampaignEditChatAccountEvidence v-else-if="part.type === 'tool-getAccountEvidence' && (part as ToolPart).state === 'output-available'" :evidence="(part as ToolPart).output as import('@coldreach/agent').AccountEvidenceResult" />
                                    <CampaignEditChatReviewCards v-else-if="part.type === 'tool-showContactReviews'" :part="part" />
                                    <CampaignEditChatCSVUploader v-else-if="part.type === 'tool-showCSVUploader'" :part="part" :chat-id="chatId" :on-upload-complete="handleCSVUploadComplete" />
                                    <CampaignEditChatExclusionUploader v-else-if="part.type === 'tool-showExclusionUploader'" :part="part" :chat-id="chatId" :on-upload-complete="handleExclusionUploadComplete" />
                                    <CampaignEditChatImageUploader v-else-if="part.type === 'tool-showImageUploader'" :part="part" @upload-complete="handleImageUploadComplete" />
                                    <CampaignEditChatToolBlock v-else-if="isGlobalAdmin || part.type !== 'tool-getCampaignEvaluation'" :part="part" :metadata="m.metadata" :active="(isLoading || streamingChat) && !interruptedExecutionId && m.id === chat.messages.at(-1)?.id" />
                                </template>
                            </template>
                            <!-- Intermediate narration — collapsed when between tool groups -->
                            <template v-else-if="group.type === 'narration'">
                                <details class="collapsible-block text-xs">
                                    <summary class="cursor-pointer select-none py-0.5 text-slate-400 hover:text-slate-600 font-medium flex items-center gap-2">
                                        <UIcon name="i-heroicons-chevron-right" class="w-3 h-3 flex-shrink-0 collapsible-chevron" />
                                        <UIcon name="i-heroicons-ellipsis-horizontal" class="w-3.5 h-3.5 flex-shrink-0" />
                                        <span>Work details</span>
                                    </summary>
                                    <div class="collapsible-content ml-1.5 pl-4 border-l border-slate-200 py-2">
                                        <div v-for="(part, pi) in group.parts" :key="pi" class="chat-markdown text-xs text-slate-500 leading-relaxed" v-html="md.render(extractThinking(part.text || '').cleaned)"></div>
                                    </div>
                                </details>
                            </template>
                        </template>
                        <div v-if="suggestedReplies?.messageId === m.id" class="mt-3 flex flex-wrap gap-2" role="group" aria-label="Suggested replies">
                            <UButton
                                v-for="action in suggestedReplies.actions"
                                :key="action.index"
                                :color="action.primary ? 'primary' : 'neutral'"
                                :variant="action.primary ? 'solid' : 'outline'"
                                size="sm"
                                class="max-w-full whitespace-normal text-left"
                                :disabled="suggestedRepliesDisabled"
                                :title="action.prompt"
                                @click="sendSuggestedReply(m.id, action.index)"
                            >
                                {{ action.label }}
                            </UButton>
                        </div>
                        <button
                            v-if="shouldShowImplementationDetailsAction(chat.messages, m, isLoading || streamingChat || recoveringStream || !!input.trim(), implementationDetailsMessageId)"
                            type="button"
                            class="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                            @click="prefillImplementationDetailsRequest"
                        >
                            <UIcon name="i-heroicons-document-magnifying-glass" class="h-3.5 w-3.5" />
                            Show implementation details
                        </button>
                    </div>
                </template>
            </div>

            <!-- Loading indicator -->
            <div v-if="compactionPhase" class="space-y-2 rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                <div class="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span role="status" aria-live="polite" class="font-medium text-slate-700">{{ compactionPhase === "compacting" ? "Compacting conversation…" : "Resuming your response…" }}</span>
                    <span class="tabular-nums text-slate-500">{{ compactionElapsed }} elapsed</span>
                </div>
                <UProgress :model-value="null" size="sm" :aria-label="compactionPhase === 'compacting' ? 'Compacting conversation' : 'Resuming your response'" />
                <p class="text-xs text-slate-500">{{ compactionPhase === "compacting" ? "Summarizing earlier messages so this conversation can continue." : "Using the summary to continue your response." }}</p>
            </div>
            <div v-else-if="isLoading || streamingChat || recoveringStream" class="flex items-center gap-1.5 py-1">
                <div class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                <div class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                <div class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                <span v-if="recoveringStream" class="text-xs text-slate-400 ml-1">Resuming response…</span>
            </div>
        </div>

        <div v-if="interruptedExecutionId" role="status" class="flex-none mx-4 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <div class="font-medium">This response was interrupted</div>
            <p class="mt-1 text-xs">Saved progress is kept. Continue from where the response stopped instead of repeating your request.</p>
            <p v-if="hasUncertainToolCalls" class="mt-1 text-xs">An action may have finished before its result was saved. Continue will check what happened without making changes.</p>
            <p v-if="savedPendingMessages.length" class="mt-1 text-xs">{{ savedPendingMessages.length }} queued message(s) are saved and will stay paused for your review.</p>
            <p v-if="submissionError" class="mt-1 text-xs text-red-700">{{ submissionError.message }}</p>
            <button type="button" :disabled="continuingResponse" @click="continueInterruptedResponse" class="mt-2 rounded bg-amber-100 px-2 py-1 text-xs font-medium hover:bg-amber-200 disabled:opacity-50">{{ continuingResponse ? "Continuing…" : "Continue" }}</button>
        </div>
        <div v-if="hasHeldMessages" role="status" class="flex-none mx-4 mb-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            <p class="font-medium">Review waiting messages before sending a new instruction.</p>
            <p class="mt-1">Continue applies all displayed waiting messages together, in order, after the latest conversation. Discard removes them without running them.</p>
            <p v-if="hasUncertainToolCalls" class="mt-1">An earlier action has an unknown outcome. Continuing waiting messages will only inspect current state, not make changes.</p>
            <details open>
                <summary class="cursor-pointer font-medium">{{ savedPendingMessages.length }} waiting message(s)</summary>
                <ol class="mt-2 list-decimal space-y-1 pl-4"><li v-for="message in savedPendingMessages" :key="message.id" class="whitespace-pre-wrap break-words">{{ message.text || "Attached message" }}</li></ol>
            </details>
            <button type="button" :disabled="!!interruptedExecutionId || currentResponseActive || continuingResponse" @click="continueQueuedMessages" class="mt-2 rounded bg-blue-100 px-2 py-1 font-medium hover:bg-blue-200 disabled:opacity-50">{{ savedPendingMessages.length === 1 ? "Continue waiting message" : "Continue waiting messages" }}</button>
            <button type="button" :disabled="continuingResponse" @click="discardWaitingMessages" class="mt-2 ml-2 rounded px-2 py-1 font-medium hover:bg-blue-100 disabled:opacity-50">Discard waiting messages</button>
        </div>

        <!-- Error indicator -->
        <div v-if="!interruptedExecutionId && (chat.error || recoveryError || submissionError)" class="flex-none mx-4 mb-2">
            <div class="rounded-lg px-3 py-2" :class="errorBannerClass">
                <div class="flex items-start space-x-2">
                    <UIcon :name="errorBannerIcon" class="flex-shrink-0 w-4 h-4 mt-0.5" :class="errorBannerIconClass" />
                    <div class="flex-1 min-w-0">
                        <div class="text-sm" :class="errorBannerTextClass">
                            {{ friendlyErrorMessage }}
                        </div>
                        <button v-if="isRetryableError && !submissionError" @click="retryLastMessage" class="mt-1.5 text-xs font-medium px-2 py-0.5 rounded transition-colors" :class="errorRetryClass">Try again</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Queued message indicator -->
        <div v-if="pendingMessage && !hasHeldMessages" class="flex-none mx-3 mb-0">
            <div class="flex items-center justify-between px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-t-lg text-xs">
                <span class="text-blue-600 flex items-center gap-1.5">
                    <UIcon :name="pendingMessage.status === 'processing' ? 'i-heroicons-bolt' : 'i-heroicons-clock'" class="w-3.5 h-3.5" />
                    {{ pendingMessage.status === "processing" ? "Working on your queued message…" : "Message queued — will send when AI finishes" }}
                </span>
                <button v-if="pendingMessage.status !== 'processing'" @click="cancelPendingMessage" :disabled="pendingMessage.status === 'saving' || pendingMessage.status === 'uncertain'" class="text-blue-400 hover:text-blue-600 transition-colors font-medium disabled:cursor-wait disabled:opacity-50">Cancel</button>
            </div>
        </div>

        <!-- Input Form -->
        <div class="flex-none px-3 py-2 chat-bg">
            <div v-if="lastUsedByOtherUserLabel" class="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <UIcon name="i-heroicons-exclamation-triangle" class="mt-0.5 h-4 w-4 flex-none text-amber-500" />
                <div>
                    <div class="font-medium">Proceed with caution</div>
                    <div>This chat was last used by {{ lastUsedByOtherUserLabel }}. Review the prior context before continuing as yourself.</div>
                </div>
            </div>
            <div v-if="chatLoadError" class="mb-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                <UIcon name="i-heroicons-exclamation-triangle" class="mt-0.5 h-4 w-4 flex-none text-red-500" />
                <div>
                    <div class="font-medium">Chat could not be loaded</div>
                    <div>{{ chatLoadError }} Switch to the correct organization or reload before sending another message.</div>
                </div>
            </div>
            <div v-if="waitingCampaigns.length" role="status" class="mb-2 space-y-1.5 px-1 text-xs text-slate-500">
                <div v-for="campaign in waitingCampaigns" :key="campaign.workSessionId" class="flex min-w-0 items-start gap-2">
                    <UIcon name="i-heroicons-arrow-path" class="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-blue-500 motion-reduce:animate-none" />
                    <span class="min-w-0">Preparing contacts for review… <NuxtLink :to="`/campaigns/${campaign.campaignId}/review`" class="break-words font-medium text-slate-600 hover:underline">{{ campaign.campaignName }}</NuxtLink></span>
                </div>
            </div>
            <SharedChatPromptInput v-model="input" :placeholder="inputPlaceholder" :loading="isLoading || streamingChat || recoveringStream" :queued="!!pendingMessage" :disabled="chatInputDisabled" :allow-empty-submit="!!stoppedUserMessage" size="sm" ref="promptInputRef" @submit="handleSubmit" @stop="stopGeneration" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { buildCSVImportCompletionMessage, buildExclusionImportCompletionMessage, type CSVImportCompletion, type ExclusionImportCompletion } from "@coldreach/utils/import-completion";
import { Chat } from "@ai-sdk/vue";
import { DefaultChatTransport, generateId, UIMessage } from "ai";
import markdownit from "markdown-it";
import type { ChatAttachedFile, ChatFilePart } from "~/components/shared/ChatPromptInput.vue";
import type { QueuedChatMessage } from "~/composables/chatHandoff";
import type { CreativeBet, CreativeBetAction, CreativeBetsData, Step1CreativeBetVariant } from "~/utils/creative-bets";
import { getChatReasoningDisplay } from "~/utils/chat-reasoning-display";
import { IMPLEMENTATION_DETAILS_PROMPT, shouldShowImplementationDetailsAction } from "~/utils/chat-message-actions";
import { chatRetryAction, resendChatMessage, sendChatMessage, type ChatStreamingState } from "~/utils/chat-stream-retry";
import { buildChatUserMessage } from "~/utils/chat-user-message";

const isGlobalAdmin = useGlobalAdmin();

const md = markdownit({ linkify: true, breaks: true });
md.renderer.rules.link_open = function (tokens, idx, options, _env, self) {
    tokens[idx].attrSet("target", "_blank");
    tokens[idx].attrSet("rel", "noopener noreferrer");
    return self.renderToken(tokens, idx, options);
};

/**
 * Pull `<thinking>…</thinking>` blocks out of the model's text so the
 * main bubble stays concise.  Returns the cleaned text, an array of
 * completed thinking blocks, and any pending (unclosed) thinking content.
 * Results are cached per input string to avoid duplicate work in the template.
 */
interface ThinkingResult {
    cleaned: string;
    thinkingBlocks: string[];
    pendingThinking: string | null;
}
const _thinkingCache = new Map<string, ThinkingResult>();
function extractThinking(text: string): ThinkingResult {
    const cached = _thinkingCache.get(text);
    if (cached) return cached;

    const thinkingBlocks: string[] = [];
    let cleaned = text.replace(/<thinking>([\s\S]*?)<\/thinking>/gi, (_match, inner) => {
        thinkingBlocks.push(inner.trim());
        return "";
    });

    // Detect unclosed <thinking> tag (still streaming)
    let pendingThinking: string | null = null;
    const pendingMatch = cleaned.match(/<thinking>([\s\S]*)$/i);
    if (pendingMatch) {
        pendingThinking = pendingMatch[1].trim();
        cleaned = cleaned.slice(0, pendingMatch.index).trim();
    }

    const result: ThinkingResult = { cleaned, thinkingBlocks, pendingThinking };
    _thinkingCache.set(text, result);
    if (_thinkingCache.size > 200) _thinkingCache.clear();
    return result;
}

/** Return only the last N lines of text */
function lastNLines(text: string, n: number): string {
    const lines = text.split("\n");
    return lines.slice(-n).join("\n");
}

/** Check if a reasoning part is the last reasoning part in a group */
function isLastReasoningInGroup(partIndex: number, groupParts: Array<{ type: string }>): boolean {
    for (let i = groupParts.length - 1; i >= 0; i--) {
        if (groupParts[i].type === "reasoning") return i === partIndex;
    }
    return false;
}

/** Check if a reasoning part is actively being streamed (last reasoning in a streaming message) */
function isActiveReasoning(messageIndex: number, partIndex: number, groupParts: Array<{ type: string }>): boolean {
    return (isLoading.value || streamingChat.value) && messageIndex === visibleMessages.value.length - 1 && isLastReasoningInGroup(partIndex, groupParts);
}

interface CreativeBetFollowupArcStep {
    stepLabel: string;
    timing?: string;
    goal: string;
    angle: string;
    cta: string;
    snippet?: string;
    avoidRepeating?: string;
}

interface CreativeBetFollowupArcData {
    campaignId: number;
    summary: string;
    bet: CreativeBet;
    steps: CreativeBetFollowupArcStep[];
    strategyNotes?: string[];
}

interface ContactReviewData {
    assignmentId: number;
    contactName: string;
    contactTitle: string;
    contactEmail?: string;
    companyName: string;
    companyDomain?: string;
    companyDescription?: string;
    snippets: Array<{ key: string; prompt: string; value: string }>;
    sequenceSteps: Array<{ index: number; subject?: string; body: string }>;
    researchSignals: Array<{ name: string; qualified: boolean; summary?: string }>;
}

interface RefineReplyContext {
    assignmentId: number;
    draftBody: string;
    contactName?: string;
    contactTitle?: string;
    companyName?: string;
}

interface ChatContext {
    page: "automatic-experiment" | "new-campaign" | "campaign-editor" | "review" | "portfolio-creative-bets" | "contact-review" | "improve-campaign" | "optimize-hit-ratio" | "review-targeting" | "refine-reply" | "extract-reply-instructions" | "initial-setup" | "default";
    icpId?: number;
    intent?: "add-must-have-filter" | "add-enrichment";
    contactReview?: ContactReviewData;
    refineReply?: RefineReplyContext;
    greeting?: string;
}

const props = withDefaults(
    defineProps<{
        context?: ChatContext;
        initialChatId?: string;
        initialPrompt?: string;
        initialMessage?: { text: string; files: ChatAttachedFile[] };
        prefillPrompt?: string;
        lastUsedByOtherUserLabel?: string;
        isDraftChat?: boolean;
    }>(),
    {
        context: () => ({ page: "default" as const }),
        isDraftChat: false,
    },
);

const emit = defineEmits<{
    "campaign-created": [campaignId: number, chatId: string];
    "campaign-launched": [campaignId: number];
}>();

const route = useRoute();

// icpId the chat is currently operating in — set once on mount, updated only on explicit switch
const icpId = ref<number | undefined>(props.context?.icpId ?? (route.params.icp ? Number(route.params.icp) : undefined));
const chatId = ref<string>();

interface ActiveCampaignChat {
    id: string;
    title: string | null;
    ownerLabel: string | null;
    ownerName: string | null;
    updatedAt: string;
    streamingState: "idle" | "streaming";
}

const campaignChats = ref<ActiveCampaignChat[]>([]);
const showOtherActiveChats = ref(false);
const stoppingOtherChatIds = ref<string[]>([]);
let campaignChatPollTimer: ReturnType<typeof setTimeout> | null = null;
const otherActiveChats = computed(() => campaignChats.value.filter(candidate =>
    candidate.id !== chatId.value && candidate.streamingState === "streaming",
));

async function refreshCampaignChats() {
    if (!icpId.value) {
        campaignChats.value = [];
        return;
    }
    try {
        // @ts-ignore TS2589: Nitro route type resolution is too deep for this endpoint.
        campaignChats.value = await $fetch<ActiveCampaignChat[]>("/api/chats", { query: { icpId: icpId.value } });
    } catch (error) {
        console.error("[chat] Failed to load concurrent campaign chats:", error);
    }
}

let chatDisposed = false;
function scheduleCampaignChatRefresh() {
    if (chatDisposed) return;
    if (campaignChatPollTimer) clearTimeout(campaignChatPollTimer);
    campaignChatPollTimer = setTimeout(async () => {
        await Promise.all([refreshCampaignChats(), refreshCompletionMessages()]);
        scheduleCampaignChatRefresh();
    }, 3000);
}

function otherChatLink(otherChatId: string) {
    return {
        path: `/chat/${otherChatId}`,
        query: { page: "campaign-editor", icpId: String(icpId.value) },
    };
}

async function stopOtherChat(otherChatId: string) {
    if (stoppingOtherChatIds.value.includes(otherChatId)) return;
    stoppingOtherChatIds.value = [...stoppingOtherChatIds.value, otherChatId];
    try {
        await $fetch("/api/chats/stop", { method: "POST", body: { chatId: otherChatId } });
        await refreshCampaignChats();
    } finally {
        stoppingOtherChatIds.value = stoppingOtherChatIds.value.filter(id => id !== otherChatId);
    }
}

async function stopAllOtherActiveChats() {
    await Promise.all(otherActiveChats.value.map(otherChat => stopOtherChat(otherChat.id)));
}

// Get all ICPs for context display
const { data: overviewICPs } = useOverviewICPs();
const currentICP = computed(() => {
    if (!icpId.value || !overviewICPs.value) return null;
    return overviewICPs.value.find((icp) => icp.id === icpId.value);
});

const { refresh: refreshICP } = icpId.value ? useICP(icpId.value) : { refresh: async () => {} };
const { refresh: refreshItems } = useItems();
const { aiModifyItemParams, refreshSequences } = useICPHooks();
const isNewCampaignFlow = computed(() => props.context?.page === "new-campaign");
const isCampaignEditorFlow = computed(() => props.context?.page === "campaign-editor");
const isReviewFlow = computed(() => props.context?.page === "review");
const isPortfolioCreativeBetsFlow = computed(() => props.context?.page === "portfolio-creative-bets");
const isContactReviewFlow = computed(() => props.context?.page === "contact-review");
const isImproveCampaignFlow = computed(() => props.context?.page === "improve-campaign");
const isRefineReplyFlow = computed(() => props.context?.page === "refine-reply");
const isOptimizeHitRatioFlow = computed(() => props.context?.page === "optimize-hit-ratio");
const isReviewTargetingFlow = computed(() => props.context?.page === "review-targeting");
const isInitialSetupFlow = computed(() => props.context?.page === "initial-setup");
const initialChatId = computed(() => (isValidUUID(props.initialChatId) ? props.initialChatId : undefined));
const input = ref("");
let implementationDetailsMessageId = ref<string>();
const loadingChat = ref(false);
const chatLoadError = ref<string | null>(null);
const checkingRetryStreamStatus = ref(false);
let retryStatusGeneration = 0;
const confirmingEligibilityPolicy = ref(false);
const chatInputDisabled = computed(() => confirmingEligibilityPolicy.value || loadingChat.value || !!chatLoadError.value || checkingRetryStreamStatus.value || !!interruptedExecutionId.value || continuingResponse.value || hasHeldMessages.value);

// Grouped AI capability suggestions for campaign editor flow (launched campaigns)
const campaignEditorGroups = [
    {
        title: "Analyze & Improve",
        actions: [
            { label: "Analyze campaign performance", icon: "i-ion-analytics", description: "Review metrics, reply rates, and what's working vs. what needs attention.", prompt: "Analyze this campaign's performance and highlight what's working and what needs attention." },
            { label: "Review outreach quality", icon: "i-ion-checkmark-circle-outline", description: "Check if recent messages are relevant, well-targeted, and well-written.", prompt: "Review the quality of my recent outreach — are the messages relevant and well-targeted?" },
            { label: "Improve reply rate", icon: "i-ion-trending-up", description: "Diagnose low reply rates and get specific, actionable suggestions.", prompt: "My reply rate could be better. Analyze what's going wrong and suggest specific improvements." },
        ],
    },
    {
        title: "Targeting & Contacts",
        actions: [
            { label: "Edit targeting criteria", icon: "i-ion-funnel-outline", description: "Adjust signals, filters, or ICP definition to improve who you reach.", prompt: "Help me edit the targeting criteria for this campaign." },
            { label: "Expand targeting", icon: "i-ion-globe-outline", description: "Broaden your account pool to reach more companies while keeping quality high.", prompt: "My account pool is too small. Help me expand targeting to reach more companies while keeping quality high." },
            { label: "Review pending contacts", icon: "i-ion-people-outline", description: "Inspect contacts waiting for approval and check if they're a good fit.", prompt: "Show me the contacts pending approval and help me decide which ones to approve." },
        ],
    },
    {
        title: "Sequences & Messaging",
        actions: [
            { label: "Write or improve sequences", icon: "i-ion-create-outline", description: "Create new steps, rewrite messaging, or add A/B test variants.", prompt: "Help me write or improve the email sequences for this campaign." },
            { label: "Personalize snippets", icon: "i-ion-code-working-outline", description: "Create or tune dynamic variables that personalize each message.", prompt: "Help me create or improve the snippet prompts to better personalize my outreach messages." },
        ],
    },
    {
        title: "Campaign Management",
        actions: [
            { label: "Clone for another channel", icon: "i-ion-copy-outline", description: "Duplicate this campaign to run on a different channel (email, LinkedIn).", prompt: "Help me clone this campaign for a different outreach channel." },
            { label: "Configure sending settings", icon: "i-ion-settings-outline", description: "Adjust mailboxes, sending schedule, or autopilot mode.", prompt: "Help me review and configure the sending settings — mailboxes, schedule, and autopilot." },
        ],
    },
];

const NEW_PRODUCT_CONTEXT_WELCOME_MESSAGE = `Let's teach Coldreach about your product.

What would you like me to learn from? You can type or paste anything below, share a website, or attach materials like decks, one-pagers, case studies, messaging docs, or call transcripts.

Useful details include **what you sell**, **who buys it**, **the problems you solve**, **what makes you different**, and **proof points or customer stories**.`;

const EXISTING_PRODUCT_CONTEXT_WELCOME_MESSAGE = `Let's update what Coldreach knows about your product.

I already have Product Context for your organization. What would you like to add, correct, or refresh? You can type or paste anything below, share a website, or attach materials like decks, one-pagers, case studies, messaging docs, or call transcripts.`;

// Structural fill-in-the-blank starters for new campaign flow
const newCampaignStarters = [
    { template: "I want to reach {job title} at {type of company}", cursorOffset: 16 },
    { template: "I'm selling {my product/service} to {target industry or audience}", cursorOffset: 13 },
    { template: "Find companies that {signal or trigger event} and reach their {role}", cursorOffset: 21 },
];

// Parse a template string into segments, marking {placeholders}
function parseTemplate(template: string): Array<{ text: string; placeholder: boolean }> {
    const segments: Array<{ text: string; placeholder: boolean }> = [];
    const regex = /\{([^}]+)\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(template)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ text: template.slice(lastIndex, match.index), placeholder: false });
        }
        segments.push({ text: match[1], placeholder: true });
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < template.length) {
        segments.push({ text: template.slice(lastIndex), placeholder: false });
    }
    return segments;
}

// Prefill the input with a template (placeholders replaced with blanks for editing)
function prefillPrompt(template: string, _cursorOffset: number) {
    // Replace {placeholder} with the placeholder text so user can edit inline
    input.value = template.replace(/\{([^}]+)\}/g, "[$1]");
    nextTick(() => {
        promptInputRef.value?.focus();
    });
}

function prefillImplementationDetailsRequest() {
    input.value = IMPLEMENTATION_DETAILS_PROMPT;
    scrollToBottom();
    nextTick(() => {
        promptInputRef.value?.focus();
    });
}

const examplePrompts = computed(() => {
    if (isNewCampaignFlow.value) return ["I want to reach CTOs at mid-market SaaS companies in the US", "Help me find marketing directors at e-commerce brands with 50-200 employees", "I'm selling cybersecurity software to financial services companies"];
    if (isReviewFlow.value) return ["Analyze my recent outreach contacts", "Review the quality of my last 24 hours of outreach", "Check if my recent contacts were well-targeted"];
    if (isPortfolioCreativeBetsFlow.value) return ["Show me distinct email and LinkedIn experiments to add", "Find the biggest gap in my active campaign portfolio", "Propose a new campaign idea that does not duplicate what is running"];
    if (isContactReviewFlow.value) return ["The messaging doesn't feel relevant to this contact — help me improve it", "Rewrite the snippet prompts to better leverage the research signals", "The tone is too aggressive, make it more conversational"];
    if (isImproveCampaignFlow.value) return ["Analyze this campaign and suggest improvements", "What's wrong with my targeting and filtering?", "Help me improve the messaging and sequences"];
    if (isOptimizeHitRatioFlow.value) return ["Why is my hit ratio so low?", "Tighten the qualification criteria to improve hit ratio", "Analyze the filtering and suggest better targeting"];
    if (isRefineReplyFlow.value) return ["The tone is too aggressive — make it softer and more conversational", "Don't mention the product directly, just offer to help with their problem", "Keep it shorter and more concise, 2-3 sentences max"];
    if (isReviewTargetingFlow.value) return ["My account pool is too small — help me expand targeting", "Review my targeting criteria and suggest how to reach more companies", "Broaden the filters while keeping quality high"];
    if (props.context?.intent === "add-must-have-filter") return ["Filter for companies that recently raised funding", "Only keep companies using a specific technology", "Exclude companies that already have a competing solution"];
    if (props.context?.intent === "add-enrichment") return ["Summarize each company's recent product launches", "Find talking points about their hiring trends", "Research their latest news and press releases"];
    return ["Create a new outbound campaign based on my business.", "This campaign is not working well. Analyze what's wrong.", "Help me improve my email subject lines for better open rates."];
});

// Helper to cast a generic part to ChatFilePart for template usage
function asFilePart(part: Record<string, unknown>): ChatFilePart {
    return part as unknown as ChatFilePart;
}

function getMetadataString(message: UIMessage, key: string): string | undefined {
    const value = (message.metadata as Record<string, unknown> | undefined)?.[key];
    return typeof value === "string" ? value : undefined;
}

function getModelId(message: UIMessage): string | undefined {
    return getMetadataString(message, "modelId");
}

function getSenderLabel(message: UIMessage): string | undefined {
    return getMetadataString(message, "senderLabel");
}

const inputPlaceholder = computed(() => {
    if (isNewCampaignFlow.value) return "Describe your ideal customer...";
    if (isInitialSetupFlow.value) return "Share a website, paste notes, or attach product materials...";
    if (isReviewFlow.value) return "Ask about your outreach...";
    if (isPortfolioCreativeBetsFlow.value) return "Ask about portfolio experiments...";
    if (isContactReviewFlow.value) return "Describe what you'd like to change...";
    if (isImproveCampaignFlow.value) return "Ask about campaign improvements...";
    if (isOptimizeHitRatioFlow.value) return "Ask about hit ratio optimization...";
    if (isRefineReplyFlow.value) return "Describe how to improve the reply...";
    if (isReviewTargetingFlow.value) return "Ask about expanding your targeting...";
    return "Type your message...";
});

const messageUpdateKey = ref(0); // Increments to force re-render when tool states change

function buildContextPrompt(): string | undefined {
    const page = props.context?.page;
    const campaignName = currentICP.value?.name || "Unknown Campaign";
    const campaignIcpId = icpId.value;
    if (page === "improve-campaign" && campaignIcpId) {
        return [
            `The user clicked View recommendation for campaign '${campaignName}' (ID: ${campaignIcpId}).`,
            ``,
            `## Creative bets workflow`,
            `This replaces the old campaign-review inbox-test flow. Do NOT call showContactReviews unless the user explicitly asks for contact review cards.`,
            `1. Load the campaign-creative-bets preset and follow its full read-only analysis workflow before showing cards. Do not shortcut straight from score to ideas.`,
            `2. Gather Product Context, campaign setup, evaluation, sequence copy, performance samples, and pending contacts as available.`,
            `3. Diagnose the stale pattern and highest-leverage creative gap before calling showCreativeBets with 2-3 distinct sequence-scoped creative bets. Keep each card decision-critical and grounded in real context; if unavailable, use a realistic placeholder first name like John and neutral company/team language instead of a generic nameless greeting or fake company facts. Do not use merge tags or em dashes in example copy, and preserve blank lines between outreach paragraphs.`,
            `4. Use sequencePreview to show concrete copy for the steps this bet would change. Include a structured subject for email Step 1 and any followup that starts a new thread; omit subject for LinkedIn and same-thread followups. Do not put \"Subject:\" inside copy. For full-sequence bets, include Step 1 plus 1-2 followups. For step1-refresh, include only Step 1. For followup-recovery, include only followup/breakup copy and make it visibly a followup. Do not call showCreativeBetFollowupArc unless the user explicitly asks for a separate followup arc.`,
            `5. Wait for the user to draft one bet, give feedback on a specific bet, select 2-3 eligible bets for a Step 1 A/B test, or request variations. Followup-only bets remain individually draftable and feedbackable but are not eligible for Step 1 A/B selection.`,
            `6. For one bet, draft only the exact sequence/snippet edits represented by that card, then ask for confirmation before mutating anything. For a Step 1 A/B request, use only each selected bet's explicit Step 1 subject and copy as a distinct first-touch variant; do not treat followups as linked experiment arms, and preserve all followups unless the user separately approves changes.`,
            `7. After approved edits are applied, follow the standard campaign-modification workflow: if the campaign was paused to make the changes, run the pre-launch check and relaunch it automatically before reporting completion.`,
        ].join("\n");
    }
    if (page === "optimize-hit-ratio" && campaignIcpId) {
        return `The user needs to optimize the hit ratio for campaign '${campaignName}' (ID: ${campaignIcpId}). The current hit ratio is low (<1%).`;
    }
    if (page === "review-targeting" && campaignIcpId) {
        return `The user's campaign '${campaignName}' (ID: ${campaignIcpId}) has a small account pool (under 1k companies). Analyze the current targeting criteria and filters, then suggest ways to expand the audience while maintaining quality.`;
    }
    if (page === "contact-review" && props.context?.contactReview) {
        return buildContactReviewContextPrompt(props.context.contactReview, campaignIcpId);
    }
    if (page === "refine-reply" && props.context?.refineReply) {
        return buildRefineReplyContextPrompt(props.context.refineReply);
    }
    return undefined;
}

function buildRefineReplyContextPrompt(data: { assignmentId: number; draftBody: string; contactName?: string; contactTitle?: string; companyName?: string }): string {
    const contactInfo = [data.contactName && `Name: ${data.contactName}`, data.contactTitle && `Title: ${data.contactTitle}`, data.companyName && `Company: ${data.companyName}`].filter(Boolean).join(", ");

    return [
        `The user is viewing a reply conversation and wants to refine the AI reply.`,
        `Assignment ID: ${data.assignmentId}`,
        contactInfo ? `Contact: ${contactInfo}` : "",
        ``,
        `## Current State`,
        data.draftBody ? `Current draft: "${data.draftBody}"` : "(No draft was generated — AI needed human input)",
        ``,
        `## Your Job`,
        `1. FIRST: Call getConversationContext with assignmentId ${data.assignmentId} to load the full conversation history, contact details, current draft status, and reply instructions. This is essential context.`,
        `2. Summarize what you see — the conversation, why the AI generated this draft (or couldn't), and what the current instructions say.`,
        `3. Listen to the user's feedback — they may provide missing info (booking links, pricing), request tone changes, or give specific instructions.`,
        `4. Update the reply instructions using storeReplyInstructions (read existing first, then merge changes surgically).`,
        `5. Call regenerateDraftReply with assignmentId ${data.assignmentId} to regenerate. This is blocking and returns the new draft text.`,
        `6. IMPORTANT: After regeneration, inspect the result carefully:`,
        `   - Show the new draft to the user`,
        `   - If decision is "NeedsHuman", explain what's still missing and ask for the info`,
        `   - If the draft could be better, suggest refinements`,
        `   - Ask the user if they want to iterate further`,
        `7. Repeat steps 3-6 until the user is satisfied.`,
        ``,
        `Be concise. Always show the regenerated reply text. The user wants quick results without leaving this screen.`,
    ]
        .filter(Boolean)
        .join("\n");
}

function buildContactReviewContextPrompt(data: ContactReviewData, campaignIcpId?: number): string {
    const snippetsSummary = data.snippets.map((s) => `- Snippet "{{${s.key}}}" (prompt: "${s.prompt}")\n  Generated value: "${s.value}"`).join("\n");

    const stepsSummary = data.sequenceSteps.map((s) => `- Step ${s.index + 1}:${s.subject ? ` Subject: "${s.subject}"` : ""}\n  Body: "${s.body}"`).join("\n");

    const researchSummary = data.researchSignals.map((s) => `- ${s.name}: ${s.qualified ? "Qualified" : "Not Qualified"}${s.summary ? ` — ${s.summary}` : ""}`).join("\n");

    return [
        `The user is reviewing pending contacts in the approval queue for campaign ID ${campaignIcpId}.`,
        `They are currently looking at a specific contact and want to iterate on the AI-generated messaging.`,
        ``,
        `## Currently Reviewed Contact`,
        `- Name: ${data.contactName}`,
        `- Title: ${data.contactTitle}`,
        `- Company: ${data.companyName}${data.companyDomain ? ` (${data.companyDomain})` : ""}`,
        data.companyDescription ? `- Description: ${data.companyDescription}` : "",
        `- Assignment ID: ${data.assignmentId}`,
        ``,
        `## Current Sequence Steps (as generated for this contact)`,
        stepsSummary,
        ``,
        `## AI Snippet Prompts and Generated Values`,
        snippetsSummary,
        ``,
        `## AI Research Signals`,
        researchSummary || "(no research signals)",
    ]
        .filter(Boolean)
        .join("\n");
}

const localQueuedMessage = ref<QueuedChatMessage | null>(null);
const stoppedUserMessage = ref<UIMessage | null>(null);
const messageChatIds = new Map<string, string>();
const draftRegeneratedHook = useDraftRegenerated();

const chatHandoff = useChatHandoff();
const claimedChat = chatHandoff.claim(initialChatId.value ?? "");
const pendingMessage = claimedChat?.queuedMessage ?? localQueuedMessage;
if (claimedChat) {
    implementationDetailsMessageId = claimedChat.implementationDetailsMessageId;
}
async function handleCompletedAssistantMessage(message: UIMessage) {
    implementationDetailsMessageId.value = message.id;
    console.log("onFinish: checking tool calls in completed message");

    // Check if the message has any tool invocations
    const tools = message.parts.filter((p) => p.type.startsWith("tool-"));
    if (!tools.length) return;

    // Process each completed tool call
    for (const toolInvocation of tools) {
        const toolName = toolInvocation.type.replace("tool-", "");
        console.log(`Processing completed tool: ${toolName}`);

        if (toolName === "updateCampaignSetup" && icpId.value) {
            await refreshICP();
        }
        if (toolName === "updateColdreachDB" && icpId.value) {
            await refreshICP();
        }
        if (toolName === "createSignal") {
            await refreshItems();
        }
        if (toolName === "updateSignal") {
            const { signalId } = (toolInvocation as Record<string, unknown>).input as { signalId: number };
            if (aiModifyItemParams) await aiModifyItemParams(signalId);
        }
        const sequenceTools = [
            "createSequence",
            "deleteSequence",
            "createSequenceStep",
            "replaceSequenceStepVariants",
            "updateSequenceStep",
            "deleteSequenceStep",
            "createSnippet",
            "updateSnippet",
            "deleteSnippet",
            "updateSequenceMailboxes",
            "updateSequenceSchedule",
            "updateSequenceLinkedInAccounts",
            "updateLinkedInConversationSuppression",
        ];
        if (sequenceTools.includes(toolName)) {
            if (refreshSequences) await refreshSequences();
        }
        if (toolName === "createCampaign") {
            const output = (toolInvocation as Record<string, unknown>).output as { campaignId?: number } | undefined;
            if (output?.campaignId) {
                icpId.value = output.campaignId;
                if (!isNewCampaignFlow.value && !isInitialSetupFlow.value && chatId.value) {
                    emit("campaign-created", output.campaignId, chatId.value);
                }
            }
        }
        if (toolName === "launchCampaign") {
            // Navigation is handled reactively during streaming (see launchCampaign watcher below).
            // Only refresh data here as a fallback if the watcher didn't fire (e.g. non-streaming context).
            const output = (toolInvocation as Record<string, unknown>).output as { launched?: boolean } | undefined;
            if (output?.launched && icpId.value) {
                await refreshICP();
                if (refreshSequences) await refreshSequences();
                await refreshNuxtData(`icp-sequences-${icpId.value}`);
                await refreshNuxtData(`icp-experiments-${icpId.value}`);
            }
        }
        if (toolName === "pauseCampaign" && icpId.value) {
            await refreshICP();
            if (refreshSequences) await refreshSequences();
            await refreshNuxtData(`icp-sequences-${icpId.value}`);
        }
        if (toolName === "regenerateDraftReply") {
            const toolInput = (toolInvocation as Record<string, unknown>).input as { assignmentId?: number } | undefined;
            const toolOutput = (toolInvocation as Record<string, unknown>).output as { regenerated?: boolean } | undefined;
            if (toolOutput?.regenerated && toolInput?.assignmentId) {
                draftRegeneratedHook.notifyRegenerated(toolInput.assignmentId);
            }
        }
    }
    // Force re-render of tool blocks to show updated state
    messageUpdateKey.value++;
    setTimeout(() => {
        promptInputRef.value?.focus();
    }, 100);
}

const chat =
    claimedChat?.chat ??
    new Chat({
        transport: new DefaultChatTransport({
            api: "/api/stream/chat",
            credentials: "include",
            prepareSendMessagesRequest: ({ id, messages }) => {
                const message = messages.at(-1);
                return {
                    body: {
                        message,
                        id,
                        chatId: (message && messageChatIds.get(message.id)) ?? chatId.value,
                        context: { page: props.context?.page ?? "default", icpId: icpId.value, contextPrompt: buildContextPrompt(), assignmentId: props.context?.contactReview?.assignmentId },
                    },
                };
            },
            prepareReconnectToStreamRequest: () => ({
                api: `/api/stream/chat?chatId=${encodeURIComponent(chatId.value ?? "")}`,
            }),
        }),
        onError: (error) => {
            console.error("Chat error:", error.message, error);
        },
        async onFinish({ message }) {
            await handleCompletedAssistantMessage(message);
        },
    });
const isLoading = computed(() => chat.status === "submitted" || chat.status === "streaming");
const streamingChat = ref(false);
let sendGeneration = 0;
const recoveringStream = ref(false);
const recoveryError = ref<Error | null>(null);
const interruptedExecutionId = ref<string | null>(null);
const continuingResponse = ref(false);
const hasUncertainToolCalls = ref(false);
const pendingMessagesHeld = ref(false);
const savedPendingMessages = ref<Array<{ id: string; text: string }>>([]);
const hasHeldMessages = computed(() => pendingMessagesHeld.value && savedPendingMessages.value.length > 0);
interface ChatCompactionStatus {
    phase: "compacting" | "replaying" | null;
    updatedAt: number;
}
const recoveredCompaction = ref<ChatCompactionStatus | null>(null);
const activeCompaction = computed(() => {
    if (!(isLoading.value || streamingChat.value || recoveringStream.value)
        || interruptedExecutionId.value || stoppedUserMessage.value || chat.error || recoveryError.value) return null;
    const lastMessage = chat.messages.at(-1);
    const live = lastMessage?.role === "assistant"
        ? (lastMessage.metadata as { compaction?: ChatCompactionStatus } | undefined)?.compaction : undefined;
    const recovered = recoveredCompaction.value;
    return (live && (!recovered || live.updatedAt >= recovered.updatedAt) ? live : recovered) ?? null;
});
const compactionPhase = computed(() => activeCompaction.value?.phase ?? null);
const compactionNow = ref(Date.now());
watch(compactionPhase, (phase, _previous, onCleanup) => {
    if (!phase) return;
    compactionNow.value = Date.now();
    const timer = setInterval(() => { compactionNow.value = Date.now(); }, 1000);
    onCleanup(() => clearInterval(timer));
});
const compactionElapsed = computed(() => {
    // The persisted phase timestamp preserves elapsed time across reloads. Replay starts its own clock.
    const seconds = Math.max(0, Math.floor((compactionNow.value - (activeCompaction.value?.updatedAt ?? compactionNow.value)) / 1000));
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
});
watch([chatId, () => chat.messages.findLast(message => message.role === "user")?.id], () => {
    recoveredCompaction.value = null;
}, { flush: "sync" });
watch([isLoading, streamingChat, recoveringStream], ([loading, streaming, recovering]) => {
    if (!loading && !streaming && !recovering) recoveredCompaction.value = null;
}, { flush: "sync" });

interface ChatRecoveryStatus {
    compaction?: ChatCompactionStatus | null;
    streamingState: ChatStreamingState;
    executionId: string | null;
    partialResponse: UIMessage | null;
    messages?: UIMessage[];
    pendingMessagesHeld: boolean;
    pendingMessages: Array<{ id: string; text: string }>;
    hasUncertainToolCalls: boolean;
    waitingCampaigns: Array<{ workSessionId: number; campaignId: number; campaignName: string }>;
}

function applyRecoveryStatus(status: ChatRecoveryStatus) {
    recoveredCompaction.value = status.streamingState === "streaming" ? status.compaction ?? null : null;
    pendingMessagesHeld.value = status.pendingMessagesHeld;
    savedPendingMessages.value = status.pendingMessages ?? [];
    hasUncertainToolCalls.value = status.hasUncertainToolCalls;
    if (status.streamingState !== "interrupted") {
        interruptedExecutionId.value = null;
        return;
    }
    interruptedExecutionId.value = status.executionId;
    stopStreamRecovery();
    streamingChat.value = false;
    void chat.stop();
    chat.clearError();
    recoveryError.value = null;
    // This is historical evidence, not a newly finished response: never run its tool UI effects.
    if (status.messages) chat.messages = status.messages;
    if (status.partialResponse && !chat.messages.some(message => message.id === status.partialResponse!.id)) {
        chat.messages = [...chat.messages, status.partialResponse];
    }
}

async function continueInterruptedResponse() {
    const expectedChatId = chatId.value;
    const executionId = interruptedExecutionId.value;
    if (!executionId || continuingResponse.value) return;
    continuingResponse.value = true;
    submissionError.value = null;
    try {
        await $fetch("/api/chats/continue", { method: "POST", body: { chatId: expectedChatId, executionId } });
        if (chatId.value !== expectedChatId) return;
        interruptedExecutionId.value = null;
        chat.messages = await $fetch<UIMessage[]>("/api/chats/messages", { query: { id: expectedChatId } });
        await resumeActiveStream();
    } catch (error) {
        if (chatId.value !== expectedChatId) return;
        submissionError.value = new Error("Could not continue the response. Please try again.");
        await refreshCompletionMessages();
    } finally {
        if (chatId.value === expectedChatId) continuingResponse.value = false;
    }
}

async function continueQueuedMessages() {
    const messages = [...savedPendingMessages.value];
    if (!messages.length || interruptedExecutionId.value || currentResponseActive.value || continuingResponse.value) return;
    const expectedChatId = chatId.value;
    const lastMessageId = chat.messages.at(-1)?.id ?? null;
    if (!window.confirm(`Apply all displayed waiting messages together now, in this order, after the latest conversation? They may be older than your latest instructions. Later messages take precedence over earlier ones. If this is no longer what you want, cancel and discard them instead.

${messages.map((message, index) => `${index + 1}. ${message.text || "Attached message"}`).join("\n\n")}`)) return;
    continuingResponse.value = true;
    submissionError.value = null;
    try {
        await $fetch("/api/chats/continue-queue", {
            method: "POST", body: { chatId: expectedChatId, messageIds: messages.map(message => message.id), lastMessageId },
        });
        if (chatId.value !== expectedChatId) return;
        await resumeActiveStream();
    } catch {
        if (chatId.value === expectedChatId) submissionError.value = new Error("The waiting messages could not be continued. Refresh and review the latest conversation before trying again.");
    } finally {
        if (chatId.value === expectedChatId) {
            continuingResponse.value = false;
            await refreshCompletionMessages();
        }
    }
}

async function discardWaitingMessages() {
    if (continuingResponse.value || !hasHeldMessages.value) return;
    const expectedChatId = chatId.value;
    const messages = [...savedPendingMessages.value];
    continuingResponse.value = true;
    submissionError.value = null;
    try {
        // Use the persisted queue, including messages restored after reload.
        for (const message of messages) {
            await $fetch("/api/chats/queue", {
                method: "DELETE", query: { chatId: expectedChatId, messageId: message.id },
            });
            if (chatId.value !== expectedChatId) return;
            if (pendingMessage.value?.message.id === message.id) pendingMessage.value = null;
        }
    } catch {
        if (chatId.value === expectedChatId) submissionError.value = new Error("Could not discard all waiting messages. Review the remaining messages and try again.");
    } finally {
        if (chatId.value === expectedChatId) {
            continuingResponse.value = false;
            await refreshCompletionMessages();
        }
    }
}
const submissionError = ref<Error | null>(null);
const currentResponseActive = computed(
    () =>
        isLoading.value ||
        streamingChat.value ||
        recoveringStream.value ||
        checkingRetryStreamStatus.value ||
        chat.messages.at(-1)?.id === implementationDetailsMessageId.value,
);
let recoveryGeneration = 0;

function setRecoveryError(expectedChatId: string) {
    if (chatId.value !== expectedChatId || chat.error) return;
    recoveryError.value = new Error("The previous response could not be recovered. Please try again.");
}

const waitingCampaigns = ref<Array<{ workSessionId: number; campaignId: number; campaignName: string }>>([]);
const persistedChatIds = new Set<string>();
const unavailableChatIds = new Set<string>();

function markChatPersisted(id: string | undefined) {
    if (!isValidUUID(id)) return;
    persistedChatIds.add(id);
    unavailableChatIds.delete(id);
}

function canPollChatStatus(id: string | undefined): id is string {
    if (!isValidUUID(id) || unavailableChatIds.has(id)) return false;
    return persistedChatIds.has(id)
        || chat.messages.some(message => message.role === "user")
        || isLoading.value
        || streamingChat.value
        || recoveringStream.value
        || !!pendingMessage.value
        || !!interruptedExecutionId.value;
}

// Completion notices are passive: never replace a local turn or run tool/navigation effects.
async function refreshCompletionMessages() {
    const canRefresh = () => !chatDisposed && !didHandoff.value
        && !loadingChat.value && !chatLoadError.value
        && !isLoading.value && !streamingChat.value && !recoveringStream.value
        && !checkingRetryStreamStatus.value && !pendingMessage.value && !stoppedUserMessage.value
        && !chat.error && !recoveryError.value && !submissionError.value;
    if (!canPollChatStatus(chatId.value) || chatDisposed || didHandoff.value || loadingChat.value || chatLoadError.value) return;
    const expectedChatId = chatId.value;
    const generation = sendGeneration;
    const currentMessages = chat.messages;
    try {
        const status = await $fetch<ChatRecoveryStatus>("/api/chats/stream-status", {
            query: { id: expectedChatId, completionOnly: "1" },
        });
        markChatPersisted(expectedChatId);
        if (chatDisposed || didHandoff.value || chatId.value !== expectedChatId) return;
        waitingCampaigns.value = status.waitingCampaigns;
        if (sendGeneration !== generation || continuingResponse.value) return;
        applyRecoveryStatus(status);
        if (status.streamingState === "interrupted") return;
        if (status.streamingState === "idle" && streamingChat.value && !isLoading.value && !recoveringStream.value) {
            streamingChat.value = false;
            await syncStoredMessages(expectedChatId);
            return;
        }
        if (status.streamingState === "streaming" && !isLoading.value && !recoveringStream.value && !continuingResponse.value) {
            void resumeActiveStream();
            return;
        }
        if (!canRefresh() || sendGeneration !== generation
            || chat.messages !== currentMessages || status.streamingState !== "idle") return;
        const existingIds = new Set(currentMessages.map(message => message.id));
        const additions = (status.messages ?? []).filter(message => message.role === "assistant"
            && message.id.startsWith("campaign-completion:") && !existingIds.has(message.id));
        if (additions.length) chat.messages = [...currentMessages, ...additions];
    } catch (error) {
        const statusCode = fetchStatusCode(error);
        if (statusCode === 404 || statusCode === 403) {
            unavailableChatIds.add(expectedChatId);
            return;
        }
        console.error("[chat] Failed to refresh campaign completion notices:", error);
    }
}

async function syncStoredMessages(expectedChatId: string) {
    const messages = await $fetch<UIMessage[]>("/api/chats/messages", { query: { id: expectedChatId } });
    markChatPersisted(expectedChatId);
    if (chatId.value !== expectedChatId) return;

    const existingMessageIds = new Set(chat.messages.map(message => message.id));
    const completedAssistantMessages = messages.filter(
        message => message.role === "assistant" && !existingMessageIds.has(message.id),
    );
    chat.messages = messages;
    for (const message of completedAssistantMessages) {
        await handleCompletedAssistantMessage(message);
    }
    pendingMessage.value = null;
    messageUpdateKey.value++;
}

async function resumeActiveStream() {
    if (recoveringStream.value || interruptedExecutionId.value || !isValidUUID(chatId.value)) return;
    const recoveryChatId = chatId.value;
    const generation = ++recoveryGeneration;
    recoveringStream.value = true;
    recoveryError.value = null;
    chat.clearError();

    try {
        // Keep recovery active until stored messages are synchronized. Clearing it before
        // the GET finishes lets the pending-message watcher start overlapping recoveries.
        const before = await $fetch<ChatRecoveryStatus>("/api/chats/stream-status", { query: { id: recoveryChatId } });
        if (generation !== recoveryGeneration || chatId.value !== recoveryChatId) return;
        applyRecoveryStatus(before);
        if (before.streamingState === "interrupted") return;
        if (before.streamingState === "streaming") await chat.resumeStream();
        if (generation !== recoveryGeneration || chatId.value !== recoveryChatId) return;
        const after = await $fetch<ChatRecoveryStatus>("/api/chats/stream-status", { query: { id: recoveryChatId } });
        if (generation !== recoveryGeneration || chatId.value !== recoveryChatId) return;
        applyRecoveryStatus(after);
        if (after.streamingState === "interrupted") return;
        // A missing Redis stream does not prove the execution finished. Keep polling its lease.
        streamingChat.value = after.streamingState === "streaming";
        if (after.streamingState === "idle") await syncStoredMessages(recoveryChatId);
        recoveryError.value = null;
        chat.clearError();
    } catch (error) {
        if (generation !== recoveryGeneration || chatId.value !== recoveryChatId) return;
        console.error("[chat] Failed to resume active stream:", error);
        setRecoveryError(recoveryChatId);
    } finally {
        if (generation === recoveryGeneration) recoveringStream.value = false;
    }
}

function stopStreamRecovery() {
    recoveryGeneration++;
    recoveringStream.value = false;
}

watch(
    chatId,
    (currentChatId, previousChatId) => {
        if (currentChatId === previousChatId || !isValidUUID(previousChatId)) return;
        waitingCampaigns.value = [];
        interruptedExecutionId.value = null;
        continuingResponse.value = false;
        pendingMessagesHeld.value = false;
        savedPendingMessages.value = [];
        hasUncertainToolCalls.value = false;
        retryStatusGeneration++;
        sendGeneration++;
        checkingRetryStreamStatus.value = false;
        pendingMessage.value = null;
        stoppedUserMessage.value = null;
        messageChatIds.clear();
        stopStreamRecovery();
        recoveryError.value = null;
        submissionError.value = null;
        streamingChat.value = false;
        chat.stop();
    },
    { flush: "sync" },
);

// Sync streaming state to globalChat so the sidebar stays open while AI is responding
const globalChat = useGlobalChat();
watch([isLoading, streamingChat, recoveringStream, checkingRetryStreamStatus], ([loading, streaming, recovering, checkingRetry]) => {
    globalChat.isStreaming.value = loading || streaming || recovering || checkingRetry;

    // The server claims queued turns after the visible stream finishes. Reconnect to the
    // newly active flight instead of waiting for a database polling cycle.
    if (!loading && !streaming && !recovering && !checkingRetry && !interruptedExecutionId.value && !pendingMessagesHeld.value && !continuingResponse.value && pendingMessage.value) {
        const pending = pendingMessage.value;
        if (pending.status === "uncertain") {
            void retryOrReconcileMessage(pending.message);
            return;
        }
        pending.status = "processing";
        if (!chat.messages.some(message => message.id === pending.message.id)) {
            chat.messages = [...chat.messages, pending.message];
        }
        void resumeActiveStream();
    }
});

// Stop the active stream and reset streaming state when the component is destroyed,
// unless this component handed off the chat to the sidebar (the stream must stay alive).
const didHandoff = ref(false);
onBeforeUnmount(() => {
    chatDisposed = true;
    if (campaignChatPollTimer) {
        clearTimeout(campaignChatPollTimer);
        campaignChatPollTimer = null;
    }
    if (didHandoff.value) return;

    stopStreamRecovery();

    if (isLoading.value || streamingChat.value) {
        chat.stop();
    }
    globalChat.isStreaming.value = false;
});

type ErrorCategory = "network" | "overloaded" | "rate-limit" | "context-length" | "auth" | "generic";

function categorizeError(msg: string): ErrorCategory {
    const lower = msg.toLowerCase();
    if (lower === "network error" || lower.includes("failed to fetch") || lower.includes("networkerror") || /terminated|econnreset|connection closed/.test(lower) || (lower.includes("connection") && (lower.includes("lost") || lower.includes("interrupted")))) return "network";
    if (lower.includes("overloaded") || lower.includes("529")) return "overloaded";
    if (lower.includes("rate") && lower.includes("limit")) return "rate-limit";
    if (lower.includes("too long") || lower.includes("too large") || lower.includes("context_length") || (lower.includes("context") && lower.includes("process")) || lower.includes("start a new chat")) return "context-length";
    if (lower.includes("authentication") || lower.includes("api key") || lower.includes("configuration error")) return "auth";
    return "generic";
}

const errorCategory = computed<ErrorCategory>(() => {
    return categorizeError(submissionError.value?.message || chat.error?.message || recoveryError.value?.message || "");
});

const friendlyErrorMessage = computed(() => {
    const msg = submissionError.value?.message || chat.error?.message || recoveryError.value?.message || "";
    switch (errorCategory.value) {
        case "network":
            return "The connection was interrupted. Some changes may already have completed. Try again to check this response before sending another request.";
        case "overloaded":
            return "The AI service is temporarily busy. Please try again in a moment.";
        case "rate-limit":
            return "Too many requests. Please wait a moment before trying again.";
        case "context-length":
            return "This conversation has grown too long for the AI to process. Please start a new chat to continue.";
        case "auth":
            return "AI service configuration issue. Please contact support.";
        default:
            return msg || "An error occurred. Please try again.";
    }
});

const isRetryableError = computed(() => errorCategory.value !== "context-length" && errorCategory.value !== "auth" && !recoveringStream.value && !checkingRetryStreamStatus.value);

const errorBannerClass = computed(() => (errorCategory.value === "overloaded" || errorCategory.value === "rate-limit" ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"));
const errorBannerIcon = computed(() => (errorCategory.value === "overloaded" || errorCategory.value === "rate-limit" ? "i-heroicons-clock" : errorCategory.value === "context-length" ? "i-heroicons-chat-bubble-bottom-center-text" : "i-heroicons-exclamation-triangle"));
const errorBannerIconClass = computed(() => (errorCategory.value === "overloaded" || errorCategory.value === "rate-limit" ? "text-amber-600" : "text-red-600"));
const errorBannerTextClass = computed(() => (errorCategory.value === "overloaded" || errorCategory.value === "rate-limit" ? "text-amber-800" : "text-red-800"));
const errorRetryClass = computed(() => (errorCategory.value === "overloaded" || errorCategory.value === "rate-limit" ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-red-100 text-red-700 hover:bg-red-200"));

async function resendLastMessage(lastUserMsg: UIMessage, expectedChatId = chatId.value) {
    if (!isValidUUID(expectedChatId) || chatId.value !== expectedChatId) return;
    markChatPersisted(expectedChatId);
    messageChatIds.set(lastUserMsg.id, expectedChatId);
    try {
        await resendChatMessage(chat, lastUserMsg);
    } finally {
        messageChatIds.delete(lastUserMsg.id);
    }
}

async function retryOrReconcileMessage(lastUserMsg: UIMessage, expectedChatId = chatId.value, expectedSendGeneration?: number) {
    if (!isValidUUID(expectedChatId)) return;
    const status = await $fetch<ChatRecoveryStatus>("/api/chats/stream-status", {
        query: { id: expectedChatId },
    });
    if (chatId.value !== expectedChatId || (expectedSendGeneration != null && expectedSendGeneration !== sendGeneration)) return;
    applyRecoveryStatus(status);
    if (status.streamingState === "interrupted") return;
    const action = chatRetryAction(status, lastUserMsg.id);
    if (action === "recover") {
        await resumeActiveStream();
        return;
    }
    if (action === "reconcile") {
        if (status.messages) chat.messages = status.messages;
        recoveryError.value = null;
        chat.clearError();
        messageUpdateKey.value++;
        return;
    }

    await resendLastMessage(lastUserMsg, expectedChatId);
}

async function retryLastMessage() {
    if (checkingRetryStreamStatus.value || recoveringStream.value || !isValidUUID(chatId.value)) return;

    const lastUserMsg = [...chat.messages].reverse().find((m) => m.role === "user");
    if (!lastUserMsg) return;

    const retryChatId = chatId.value;
    const generation = ++retryStatusGeneration;
    checkingRetryStreamStatus.value = true;
    recoveryError.value = null;
    try {
        await retryOrReconcileMessage(lastUserMsg, retryChatId);
    } catch (error) {
        if (chatId.value !== retryChatId) return;
        if (fetchStatusCode(error) === 404) {
            try {
                await resendLastMessage(lastUserMsg, retryChatId);
            } catch {
                setRecoveryError(retryChatId);
            }
            return;
        }
        console.error("[chat] Failed to verify server stream status before retrying:", error);
        setRecoveryError(retryChatId);
    } finally {
        if (generation === retryStatusGeneration) checkingRetryStreamStatus.value = false;
    }
}

async function buildUIMessageFromPending(
    pending: { text: string; files: ChatAttachedFile[] },
    id = generateId(),
): Promise<UIMessage> {
    return buildChatUserMessage({
        ...pending,
        id,
        uploadImage: async (image, filename) => {
            const formData = new FormData();
            formData.append("image", image, filename);
            return $fetch<{ url: string }>("/api/media/upload-image", {
                method: "POST",
                body: formData,
            });
        },
    });
}

async function queueMessage(text: string, files: ChatAttachedFile[], preparedMessage?: UIMessage) {
    const queueChatId = chatId.value;
    if (!isValidUUID(queueChatId) || pendingMessage.value || hasHeldMessages.value || interruptedExecutionId.value) return;
    markChatPersisted(queueChatId);

    const message = preparedMessage ?? await buildUIMessageFromPending({ text, files });
    const queued: QueuedChatMessage = { message, text, files, status: "saving" };
    pendingMessage.value = queued;
    recoveryError.value = null;
    try {
        const result = await $fetch<{ ok: true; status: "queued" | "processed" | "existing" }>("/api/chats/queue", {
            method: "POST",
            body: {
                chatId: queueChatId,
                message,
                context: {
                    page: props.context?.page ?? "default",
                    icpId: icpId.value,
                    contextPrompt: buildContextPrompt(),
                    assignmentId: props.context?.contactReview?.assignmentId,
                },
            },
        });
        if (chatId.value === queueChatId && pendingMessage.value?.message.id === message.id) {
            pendingMessage.value.status = result.status === "queued" ? "queued" : "processing";
            chat.clearError();
            if (
                pendingMessage.value.status === "processing" &&
                !isLoading.value &&
                !streamingChat.value &&
                !recoveringStream.value &&
                !checkingRetryStreamStatus.value
            ) {
                if (!chat.messages.some(storedMessage => storedMessage.id === message.id)) {
                    chat.messages = [...chat.messages, message];
                }
                void resumeActiveStream();
            }
        }
    } catch (error) {
        if (chatId.value !== queueChatId || pendingMessage.value?.message.id !== message.id) return;
        if (fetchStatusCode(error) === 409) {
            pendingMessage.value = null;
            await refreshCompletionMessages();
            throw new Error("Continue or discard the waiting messages before sending a new instruction.");
        }
        if (fetchStatusCode(error) === 413) {
            pendingMessage.value = null;
            throw new Error("This message and its attachments exceed the chat limit. Shorten it or use Import contacts/companies for a full list.");
        }
        pendingMessage.value.status = "uncertain";
        console.error("[chat] Queue request failed; reconciling by durable message ID:", error);
        recoveryError.value = new Error("Verifying whether your queued message was received…");
        if (!isLoading.value && !streamingChat.value && !recoveringStream.value) {
            void retryOrReconcileMessage(message, queueChatId);
        }
    }
}

function isAlreadyStreamingError(error: unknown): boolean {
    return (error instanceof Error ? error.message : String(error)).includes("Chat is already streaming");
}

function userMessageText(message: UIMessage): string {
    return message.parts
        .filter((part) => part.type === "text")
        .map((part) => (part as { type: "text"; text: string }).text)
        .join("\n");
}

async function sendMessageToChat(text: string, allFiles: ChatAttachedFile[]) {
    if (loadingChat.value || chatLoadError.value || interruptedExecutionId.value || continuingResponse.value || hasHeldMessages.value) return;
    if (isLoading.value || streamingChat.value || recoveringStream.value || checkingRetryStreamStatus.value) {
        await queueMessage(text, allFiles);
        return;
    }

    const sendChatId = chatId.value;
    if (!isValidUUID(sendChatId)) return;
    const generation = ++sendGeneration;
    recoveryError.value = null;
    stoppedUserMessage.value = null;
    implementationDetailsMessageId.value = undefined;

    // User explicitly sent a message — scroll to bottom so they see the response
    isNearBottom.value = true;
    scrollToBottom();
    streamingChat.value = true;

    let userMessage: UIMessage | undefined;
    try {
        userMessage = await buildUIMessageFromPending({ text, files: allFiles });
        if (generation !== sendGeneration || chatId.value !== sendChatId || userMessage.parts.length === 0) return;

        messageChatIds.set(userMessage.id, sendChatId);
        markChatPersisted(sendChatId);
        await sendChatMessage(chat, userMessage);

        if (generation === sendGeneration && chatId.value === sendChatId && globalChat.chatId.value === sendChatId) {
            globalChat.isDraftChat.value = false;
        }
    } catch (error) {
        if (userMessage && isAlreadyStreamingError(error)) {
            chat.clearError();
            await queueMessage(text, allFiles, userMessage);
        } else if (error !== chat.error) {
            throw error;
        }
        // Leave stream errors in the chat's status-first retry path. Classify them before
        // finally lets queued-response recovery clear chat.error, not in handleSubmit.
    } finally {
        if (userMessage) messageChatIds.delete(userMessage.id);
        if (generation === sendGeneration && chatId.value === sendChatId) {
            streamingChat.value = false;
            messageUpdateKey.value++; // Force re-render of all message parts
        }
    }
}

interface SuggestedRepliesOutput {
    actions: Array<{ label: string; prompt: string }>;
    primaryIndex: number | null;
}

const selectedSuggestionMessageId = ref<string>();
const suggestedReplies = computed(() => {
    const message = chat.messages.at(-1);
    if (message?.role !== "assistant" || chatInputDisabled.value || isLoading.value || streamingChat.value || recoveringStream.value || stoppedUserMessage.value || chat.error || recoveryError.value || submissionError.value) return null;
    const part = [...message.parts].reverse().find(part => part.type === "tool-showSuggestedReplies" && part.state === "output-available");
    if (!part || !("output" in part)) return null;
    const output = part.output as SuggestedRepliesOutput;
    return {
        messageId: message.id,
        actions: output.actions.map((action, index) => ({ ...action, index, primary: index === output.primaryIndex }))
            .sort((a, b) => Number(b.primary) - Number(a.primary)),
    };
});
const suggestedRepliesDisabled = computed(() =>
    !!input.value.trim() || !!promptInputRef.value?.selectedFiles.length || !!pendingMessage.value || savedPendingMessages.value.length > 0 || selectedSuggestionMessageId.value === suggestedReplies.value?.messageId,
);

async function sendSuggestedReply(messageId: string, actionIndex: number) {
    // Recheck at click time: ordinary chat submissions queue while busy, but stale
    // suggestions must not enqueue a second instruction or replace a user's draft.
    const suggestions = suggestedReplies.value;
    if (!suggestions || suggestions.messageId !== messageId || suggestedRepliesDisabled.value) return;
    const action = suggestions.actions.find(action => action.index === actionIndex);
    if (!action) return;
    selectedSuggestionMessageId.value = messageId;
    const expectedChatId = chatId.value;
    try {
        await sendMessageToChat(action.prompt, []);
    } catch (error) {
        if (chatId.value !== expectedChatId) return;
        input.value = action.prompt;
        submissionError.value = error instanceof Error ? error : new Error("Could not send the suggested reply.");
    }
}

const eligibilityPolicyConfirmationState = reactive<Record<string, { saving: boolean; error: string | null }>>({});
function eligibilityPolicyConfirmationKey(assistantMessageId: string, part: ToolPart): string {
    return `${chatId.value}:${assistantMessageId}:${part.toolCallId ?? ""}`;
}
function canConfirmEligibilityPolicy(assistantMessageId: string, part: ToolPart): boolean {
    if (chatInputDisabled.value || isLoading.value || streamingChat.value || recoveringStream.value || !chatId.value || !part.toolCallId) return false;
    if (eligibilityPolicyConfirmationState[eligibilityPolicyConfirmationKey(assistantMessageId, part)]?.error) return false;
    const output = part.output as Record<string, unknown> | undefined;
    if (part.state !== "output-available" || output?.status !== "awaiting-confirmation") return false;
    const latest = chat.messages.at(-1);
    return latest?.role === "assistant" && latest.id === assistantMessageId && latest.parts.includes(part as UIMessage["parts"][number]);
}
async function reloadEligibilityPolicy(assistantMessageId: string, part: ToolPart) {
    if (confirmingEligibilityPolicy.value || isLoading.value || streamingChat.value || recoveringStream.value || !chatId.value) return;
    const expectedChatId = chatId.value;
    const key = eligibilityPolicyConfirmationKey(assistantMessageId, part);
    confirmingEligibilityPolicy.value = true;
    eligibilityPolicyConfirmationState[key] = { saving: true, error: null };
    try {
        const messages = await $fetch<UIMessage[]>("/api/chats/messages", { query: { id: expectedChatId } });
        if (chatId.value === expectedChatId && !streamingChat.value && !isLoading.value) {
            chat.messages = messages;
            messageUpdateKey.value++;
        }
    } catch {
        eligibilityPolicyConfirmationState[key].error = "The saved proposal could not be reloaded. Reload before trying to confirm again.";
    } finally {
        eligibilityPolicyConfirmationState[key].saving = false;
        confirmingEligibilityPolicy.value = false;
    }
}
async function confirmEligibilityPolicy(assistantMessageId: string, part: ToolPart) {
    if (!canConfirmEligibilityPolicy(assistantMessageId, part)) return;
    const expectedChatId = chatId.value!;
    const key = eligibilityPolicyConfirmationKey(assistantMessageId, part);
    confirmingEligibilityPolicy.value = true;
    eligibilityPolicyConfirmationState[key] = { saving: true, error: null };
    try {
        await $fetch("/api/chats/campaign-eligibility-policy/confirm", {
            method: "POST", retry: 0,
            // The server reads the exact persisted proposal. Never send client-authored policy flags.
            body: { chatId: expectedChatId, assistantMessageId, toolCallId: part.toolCallId },
        });
        const messages = await $fetch<UIMessage[]>("/api/chats/messages", { query: { id: expectedChatId } });
        if (chatId.value === expectedChatId && !streamingChat.value && !isLoading.value) {
            chat.messages = messages;
            messageUpdateKey.value++;
        }
    } catch (cause) {
        const detail = cause as { data?: { message?: string }; statusMessage?: string };
        eligibilityPolicyConfirmationState[key].error = `${detail?.data?.message || detail?.statusMessage || "Confirmation could not be verified."} Reload the saved proposal before trying again.`;
    } finally {
        eligibilityPolicyConfirmationState[key].saving = false;
        confirmingEligibilityPolicy.value = false;
    }
}

function isProductContextProposalPart(part: ToolPart): boolean {
    return part.type === "tool-proposeProductContext" && part.state === "output-available" && typeof (part.output as Record<string, unknown> | undefined)?.proposalId === "string";
}

const productContextConfirmationState = reactive<Record<string, { saving: boolean; error: string | null }>>({});

function productContextConfirmationKey(assistantMessageId: string, part: ToolPart): string {
    return `${assistantMessageId}:${part.toolCallId ?? ""}`;
}

function canConfirmProductContextProposal(part: ToolPart): boolean {
    if (isLoading.value || streamingChat.value || recoveringStream.value) return false;
    const output = part.output as Record<string, unknown> | undefined;
    if (output?.status === "saved") return false;
    const lastMessage = chat.messages.at(-1);
    return lastMessage?.role === "assistant" && lastMessage.parts.includes(part as UIMessage["parts"][number]);
}

function fetchStatusCode(error: unknown): number | undefined {
    if (typeof error !== "object" || error == null) return undefined;
    const candidate = error as { statusCode?: unknown; response?: { status?: unknown } };
    if (typeof candidate.statusCode === "number") return candidate.statusCode;
    return typeof candidate.response?.status === "number" ? candidate.response.status : undefined;
}

async function confirmProductContextProposal(assistantMessageId: string, part: ToolPart) {
    if (chatInputDisabled.value || isLoading.value || streamingChat.value || recoveringStream.value) return;
    if (!chatId.value || !part.toolCallId) return;

    const key = productContextConfirmationKey(assistantMessageId, part);
    productContextConfirmationState[key] = { saving: true, error: null };
    try {
        await $fetch("/api/chats/product-context/confirm", {
            method: "POST",
            body: {
                chatId: chatId.value,
                assistantMessageId,
                toolCallId: part.toolCallId,
            },
        });
        chat.messages = (await $fetch("/api/chats/messages", {
            query: { id: chatId.value },
        })) as UIMessage[];
        messageUpdateKey.value++;
    } catch (error) {
        const status = fetchStatusCode(error);
        productContextConfirmationState[key].error = status === 409 ? "Product Context changed after this proposal was created. Ask Coldreach to create a fresh proposal before saving." : "Product Context could not be saved. Please retry or create a fresh proposal.";
    } finally {
        productContextConfirmationState[key].saving = false;
    }
}

const writingStyleConfirmationState = reactive<Record<string, { saving: boolean; error: string | null }>>({});

function writingStyleConfirmationKey(assistantMessageId: string, part: ToolPart): string {
    return `${assistantMessageId}:${part.toolCallId ?? ""}`;
}

function canConfirmWritingStyleProposal(part: ToolPart): boolean {
    if (isLoading.value || streamingChat.value || recoveringStream.value) return false;
    const output = part.output as Record<string, unknown> | undefined;
    if ((output?.status === "published" && output?.regenerationStatus !== "pending") || output?.status === "regeneration-started") return false;
    const lastMessage = chat.messages.at(-1);
    return lastMessage?.role === "assistant" && lastMessage.parts.includes(part as UIMessage["parts"][number]);
}

async function confirmWritingStyleProposal(assistantMessageId: string, part: ToolPart, writingStyle?: string) {
    if (chatInputDisabled.value || isLoading.value || streamingChat.value || recoveringStream.value) return;
    if (!chatId.value || !part.toolCallId) return;
    const key = writingStyleConfirmationKey(assistantMessageId, part);
    writingStyleConfirmationState[key] = { saving: true, error: null };
    try {
        await $fetch("/api/chats/product-context/confirm", {
            method: "POST",
            body: {
                chatId: chatId.value,
                assistantMessageId,
                toolCallId: part.toolCallId,
                kind: "writing-style",
                writingStyle,
            },
        });
        chat.messages = (await $fetch("/api/chats/messages", { query: { id: chatId.value } })) as UIMessage[];
        messageUpdateKey.value++;
    } catch (error) {
        const status = fetchStatusCode(error);
        writingStyleConfirmationState[key].error = status === 409
            ? "Writing Style changed after this proposal was created. Ask Coldreach to create a fresh proposal."
            : "Writing Style action could not be confirmed. Please retry or create a fresh proposal.";
    } finally {
        writingStyleConfirmationState[key].saving = false;
    }
}

async function handleSubmit(payload?: { text: string; files: ChatAttachedFile[] }) {
    if (chatInputDisabled.value) return;

    const text = payload?.text ?? input.value;
    const allFiles = payload?.files ?? [];
    if (!text && allFiles.length === 0 && !stoppedUserMessage.value) return;

    submissionError.value = null;
    input.value = "";

    const stoppedMessage = stoppedUserMessage.value;
    if (stoppedMessage && allFiles.length === 0 && text === userMessageText(stoppedMessage)) {
        recoveryError.value = null;
        stoppedUserMessage.value = null;
        implementationDetailsMessageId.value = undefined;
        isNearBottom.value = true;
        scrollToBottom();
        const retryChatId = chatId.value;
        if (!isValidUUID(retryChatId)) return;
        const generation = ++sendGeneration;
        streamingChat.value = true;
        try {
            try {
                await retryOrReconcileMessage(stoppedMessage, retryChatId, generation);
            } catch (error) {
                if (chatId.value !== retryChatId || generation !== sendGeneration) return;
                if (fetchStatusCode(error) !== 404) {
                    setRecoveryError(retryChatId);
                    return;
                }
                await resendLastMessage(stoppedMessage, retryChatId);
            }
            if (globalChat.chatId.value === retryChatId) globalChat.isDraftChat.value = false;
        } finally {
            if (generation === sendGeneration && chatId.value === retryChatId) {
                streamingChat.value = false;
                messageUpdateKey.value++;
            }
        }
        return;
    }

    try {
        // If currently streaming or recovering an active server stream, persist the new turn immediately.
        if (isLoading.value || streamingChat.value || recoveringStream.value) {
            await queueMessage(text, allFiles);
            return;
        }

        stoppedUserMessage.value = null;
        await sendMessageToChat(text, allFiles);
    } catch (error) {
        input.value = text;
        submissionError.value = error instanceof Error ? error : new Error("The message could not be sent.");
    }
}

async function stopGeneration() {
    sendGeneration++;
    streamingChat.value = false;

    // Preserve the durable turn so an unchanged resend reuses its ID and full parts.
    const lastUserMsg = [...chat.messages].reverse().find((m) => m.role === "user");
    stoppedUserMessage.value = lastUserMsg ?? null;
    if (lastUserMsg) {
        const text = userMessageText(lastUserMsg);
        if (text) input.value = text;
    }

    const activeChatId = chatId.value;
    stopStreamRecovery();
    if (isValidUUID(activeChatId)) {
        await $fetch("/api/chats/stop", { method: "POST", body: { chatId: activeChatId } }).catch(error => {
            console.error("[chat] Failed to stop server generation:", error);
        });
    }
    await chat.stop();
}

async function cancelPendingMessage() {
    const pending = pendingMessage.value;
    if (!pending || pending.status === "saving" || !isValidUUID(chatId.value)) return;

    try {
        const result = await $fetch<{ ok: true; removed: boolean }>("/api/chats/queue", {
            method: "DELETE",
            query: { chatId: chatId.value, messageId: pending.message.id },
        });
        if (!result.removed || pendingMessage.value?.message.id !== pending.message.id) return;
        input.value = pending.text;
        pendingMessage.value = null;
        await refreshCompletionMessages();
    } catch (error) {
        console.error("[chat] Failed to cancel queued message:", error);
        recoveryError.value = new Error("The queued message could not be cancelled.");
    }
}

async function handleAIScoreImprove() {
    await sendMessageToChat("Review my campaign narration: inspect Attention and Credibility separately, then Understanding, Next step and Follow-ups. Use the sample reactions and evidence, not a combined score or reply prediction. Ask before applying changes.", []);
}

function formatCreativeBetForPrompt(bet: CreativeBet): string {
    return [
        `Bet: ${bet.label}`,
        bet.theme ? `Theme: ${bet.theme}.` : "",
        bet.focus ? `Focus: ${bet.focus}.` : "",
        bet.rationale ? `Rationale: ${bet.rationale}` : "",
        `Preview copy:\n${bet.preview}`,
        bet.sequencePreview?.length ? `Sequence preview copy:\n${bet.sequencePreview.map((step) => [`- ${step.stepLabel}${step.timing ? ` (${step.timing})` : ""}:`, step.subject ? `Subject: ${step.subject}` : "", step.copy].filter(Boolean).join("\n")).join("\n\n")}` : "",
    ]
        .filter(Boolean)
        .join("\n\n");
}

async function handleCreativeBetDraft(bet: CreativeBet, data: CreativeBetsData) {
    await sendMessageToChat(
        [
            `[Creative Bet Draft Requested] User selected one bet to draft for campaign ${data.campaignId}.`,
            formatCreativeBetForPrompt(bet),
            `Draft the exact sequence/snippet edits represented by this card. Limit the proposal to steps explicitly shown in sequencePreview. If no sequencePreview is present, use preview according to the declared focus: followup-recovery remains followup-only; other focuses use it as Step 1 copy. Present the exact proposed edits and ask for confirmation before using any state-mutating tools. Do not mutate campaign configuration yet, and do not call showCreativeBetFollowupArc unless the user explicitly asks for a separate followup arc.`,
        ].join("\n\n"),
        [],
    );
}

function handleCreativeBetFeedback(bet: CreativeBet) {
    input.value = `Feedback on "${bet.label}": `;
    scrollToBottom();
}

async function handleCreativeBetStep1AbTestDraft(variants: Step1CreativeBetVariant[], data: CreativeBetsData) {
    await sendMessageToChat(
        [
            `[Creative Bet Step 1 A/B Test Requested] User selected ${variants.length} first-touch variants for campaign ${data.campaignId}.`,
            variants.map((variant, index) => [`Variant ${index + 1}: ${variant.bet.label} (card ${variant.betIndex + 1})`, variant.bet.rationale ? `Rationale: ${variant.bet.rationale}` : "", variant.step1Subject ? `Step 1 subject: ${variant.step1Subject}` : "", `Step 1 body:\n${variant.step1Copy}`].filter(Boolean).join("\n")).join("\n\n---\n\n"),
            `Draft exact edits for a Step 1-only A/B test. Keep each selected Step 1 subject and body as a distinct variant; do not discard or blend structured subjects. Inspect the sequence provider and actual first-message step before proposing mutations. Email Bison, Email Bison Personal, and LinkedIn message/InMail steps support this operation; Smartlead does not, so explain the limitation and do not attempt it. Resolve the correct stepIndex from the sequence instead of assuming index 0, and never alter a LinkedIn connection-request step. Do not treat any followup preview as part of a linked experiment arm. Preserve every followup step unchanged unless the user separately reviews and approves followup edits. Present the exact proposed Step 1 changes and ask for confirmation before using any state-mutating tools. After confirmation, call replaceSequenceStepVariants once with the exact ordered 2-3 selected subject/body pairs so the target step contains exactly those arms and every unselected existing arm is removed. If the campaign must be paused to apply the approved edits, follow the standard campaign-modification workflow and relaunch it automatically before reporting completion.`,
        ].join("\n\n"),
        [],
    );
}

async function handleCreativeBetFollowupArcDraftEdits(data: CreativeBetFollowupArcData) {
    await sendMessageToChat(
        [
            `[Creative Bet Followup Arc Approved] User wants exact campaign edits for bet "${data.bet.label}" on campaign ${data.campaignId}.`,
            `Selected bet preview:\n${data.bet.preview}`,
            `Followup arc summary: ${data.summary}`,
            `Followup steps:\n${data.steps
                .map((step) =>
                    [`- ${step.stepLabel}${step.timing ? ` (${step.timing})` : ""}`, `  Goal: ${step.goal}`, `  Angle: ${step.angle}`, `  CTA: ${step.cta}`, step.snippet ? `  Snippet: ${step.snippet}` : "", step.avoidRepeating ? `  Avoid repeating: ${step.avoidRepeating}` : ""]
                        .filter(Boolean)
                        .join("\n"),
                )
                .join("\n")}`,
            data.strategyNotes?.length ? `Strategy notes:\n${data.strategyNotes.map((note) => `- ${note}`).join("\n")}` : "",
            `Draft exact sequence/snippet edits that implement this first-touch bet and followup arc. Ask for confirmation before using any state-mutating tools.`,
        ]
            .filter(Boolean)
            .join("\n\n"),
        [],
    );
}

async function handleCreativeBetVariation(action: CreativeBetAction, data: CreativeBetsData) {
    await sendMessageToChat([`[Creative Bet Variation Requested] User wants "${action.label}" options for campaign ${data.campaignId}.`, action.prompt, `Generate revised creative bets with showCreativeBets. Do not mutate campaign configuration.`].join("\n\n"), []);
}

async function handleCSVUploadComplete(result: CSVImportCompletion & { importSessionId?: string; chatId?: string }) {
    if (result.importSessionId) {
        await $fetch(`/api/imports/${result.importSessionId}/notify-chat`, { method: "POST" });
        if (result.chatId !== chatId.value) return;
        await refreshCompletionMessages();
        if (result.chatId && !isLoading.value && !streamingChat.value && !recoveringStream.value) await syncStoredMessages(result.chatId);
    } else {
        await sendMessageToChat(buildCSVImportCompletionMessage(result), []);
    }

    // Refresh campaign data if attached.
    if (result.campaignId && icpId.value) {
        await refreshICP();
        if (refreshSequences) await refreshSequences();
    }
}

async function handleExclusionUploadComplete(result: ExclusionImportCompletion & { importSessionId?: string; chatId?: string }) {
    if (result.importSessionId) {
        await $fetch(`/api/imports/${result.importSessionId}/notify-chat`, { method: "POST" });
        if (result.chatId !== chatId.value) return;
        await refreshCompletionMessages();
        if (result.chatId && !isLoading.value && !streamingChat.value && !recoveringStream.value) await syncStoredMessages(result.chatId);
    } else {
        await sendMessageToChat(buildExclusionImportCompletionMessage(result), []);
    }
}

async function handleImageUploadComplete(result: { imageUrl: string; fileName: string }) {
    await sendMessageToChat(`[Image Upload Complete] Image "${result.fileName}" uploaded successfully. URL: ${result.imageUrl}`, []);
}

function applyIntentPreFill(intent: "add-must-have-filter" | "add-enrichment") {
    if (intent === "add-must-have-filter") {
        input.value = "Create a must-have filter that ";
    } else if (intent === "add-enrichment") {
        input.value = "Research for personalization: ";
    }
}

onBeforeMount(async () => {
    // Chat was handed off from another component (e.g. full-page → sidebar during launch).
    // The instance already has the right messages and an active stream — skip all init logic.
    if (claimedChat) {
        chatId.value = initialChatId.value;
        markChatPersisted(chatId.value);
        return;
    }

    if (props.context?.intent) {
        // Intent-driven: start fresh with pre-filled input using the provided draft chat id.
        chatId.value = initialChatId.value || generateUUID();
        applyIntentPreFill(props.context.intent);
    } else if (isNewCampaignFlow.value) {
        // Always start fresh for new-campaign flow
        chatId.value = initialChatId.value || generateUUID();
    } else if (isReviewFlow.value || isPortfolioCreativeBetsFlow.value || isImproveCampaignFlow.value || isOptimizeHitRatioFlow.value || isReviewTargetingFlow.value) {
        chatId.value = initialChatId.value || generateUUID();
        // Auto-send if an initial prompt is provided (e.g., from status badge click)
        if (props.initialPrompt) {
            const message = props.initialPrompt;
            nextTick(async () => {
                input.value = message;
                await handleSubmit();
            });
        } else if (props.prefillPrompt) {
            // Prefill the input without auto-sending so the user can review/edit first
            nextTick(() => {
                input.value = props.prefillPrompt!;
            });
        }
    } else if (isInitialSetupFlow.value) {
        // Persisted conversations always win. A local welcome is shown only for a genuinely new UUID.
        chatId.value = initialChatId.value || generateUUID();
        let showLocalWelcome = !initialChatId.value;
        if (initialChatId.value) {
            loadingChat.value = true;
            try {
                chat.messages = (await $fetch("/api/chats/messages", {
                    query: { id: initialChatId.value },
                })) as UIMessage[];
                markChatPersisted(initialChatId.value);
                chatLoadError.value = null;
            } catch (error) {
                chat.messages = [];
                if (fetchStatusCode(error) === 404) {
                    showLocalWelcome = true;
                    chatLoadError.value = null;
                } else {
                    chatLoadError.value = "This chat could not be loaded.";
                }
            } finally {
                loadingChat.value = false;
            }
        }

        if (showLocalWelcome && !chatLoadError.value) {
            const organization = await $fetch("/api/organization");
            const productContext = organization.productContext ?? organization.salesContext;
            const welcomeMessage = productContext?.trim() ? EXISTING_PRODUCT_CONTEXT_WELCOME_MESSAGE : NEW_PRODUCT_CONTEXT_WELCOME_MESSAGE;
            chat.messages = [
                {
                    id: generateUUID(),
                    role: "assistant" as const,
                    parts: [{ type: "text" as const, text: welcomeMessage }],
                },
            ];
        }
    } else if (props.initialMessage || props.initialPrompt) {
        chatId.value = initialChatId.value || generateUUID();
        // Load the durable turn before sending a landing-page message or legacy URL prompt
        // so remounting cannot replay completed campaign changes.
        loadingChat.value = true;
        try {
            chat.messages = await $fetch<UIMessage[]>("/api/chats/messages", { query: { id: chatId.value } });
            markChatPersisted(chatId.value);
        } catch (error) {
            if (fetchStatusCode(error) !== 404) {
                chatLoadError.value = "This chat could not be loaded.";
                return;
            }
        } finally {
            loadingChat.value = false;
        }
        if (!chat.messages.some(message => message.role === "user")) {
            nextTick(async () => {
                await handleSubmit(props.initialMessage ?? { text: props.initialPrompt!, files: [] });
            });
        }
    } else if (props.context?.greeting) {
        // Start fresh with an assistant greeting (no generation, just a static message)
        chatId.value = initialChatId.value || generateUUID();
        chat.messages = [
            {
                id: generateUUID(),
                role: "assistant" as const,
                parts: [{ type: "text" as const, text: props.context.greeting }],
            },
        ];
    } else if (isCampaignEditorFlow.value && !props.initialPrompt) {
        // Campaign editor — try to resume the most recent chat for this ICP
        if (initialChatId.value) {
            chatId.value = initialChatId.value;
            // A draft URL can outlive the first send. Load the persisted turn when it exists so
            // a reload can reconnect to its active stream; only a genuine 404 remains a draft.
            loadingChat.value = true;
            try {
                chat.messages = (await $fetch("/api/chats/messages", { query: { id: initialChatId.value } })) as UIMessage[];
                markChatPersisted(initialChatId.value);
                chatLoadError.value = null;
            } catch (error) {
                chat.messages = [];
                chatLoadError.value = props.isDraftChat && fetchStatusCode(error) === 404
                    ? null
                    : "This chat was not found in the current organization.";
            }
            loadingChat.value = false;
        } else if (icpId.value) {
            // No explicit chatId — try to resume the most recent chat for this ICP
            loadingChat.value = true;
            try {
                const chats = await $fetch("/api/chats", { query: { icpId: icpId.value } });
                const recent = (chats as Array<{ id: string; messagesCount: number }>).find((c) => c.messagesCount > 0);
                if (recent) {
                    chatId.value = recent.id;
                    chat.messages = (await $fetch("/api/chats/messages", { query: { id: recent.id } })) as UIMessage[];
                    markChatPersisted(recent.id);
                } else {
                    chatId.value = generateUUID();
                }
                chatLoadError.value = null;
            } catch {
                chatId.value = generateUUID();
                chatLoadError.value = null;
            }
            loadingChat.value = false;
        } else {
            chatId.value = generateUUID();
        }
    } else if (initialChatId.value) {
        chatId.value = initialChatId.value;
        loadingChat.value = true;
        try {
            chat.messages = (await $fetch("/api/chats/messages", { query: { id: initialChatId.value } })) as UIMessage[];
            markChatPersisted(initialChatId.value);
            chatLoadError.value = null;
        } catch (error) {
            chat.messages = [];
            chatLoadError.value = props.isDraftChat && fetchStatusCode(error) === 404
                ? null
                : "This chat was not found in the current organization.";
        }
        loadingChat.value = false;
    } else {
        // No specific chat — start fresh
        chatId.value = generateUUID();
        chatLoadError.value = null;
    }

    if (globalChat.isOpen.value && !isValidUUID(globalChat.chatId.value) && isValidUUID(chatId.value)) {
        globalChat.useDraftChat(chatId.value);
    }

    // Check if a previous stream is still running (e.g. user navigated away mid-stream)
    if (canPollChatStatus(chatId.value) && chat.messages.length > 0) {
        try {
            const status = await $fetch<ChatRecoveryStatus>("/api/chats/stream-status", {
                query: { id: chatId.value },
            });
            markChatPersisted(chatId.value);
            applyRecoveryStatus(status);
            if (status.streamingState === "streaming") {
                await resumeActiveStream();
            }
        } catch (error) {
            const statusCode = fetchStatusCode(error);
            if ((statusCode === 404 || statusCode === 403) && isValidUUID(chatId.value)) {
                unavailableChatIds.add(chatId.value);
            }
        }
    }
});

watch(icpId, async () => {
    showOtherActiveChats.value = false;
    await refreshCampaignChats();
});

// Scroll to bottom after mount if messages were pre-loaded (e.g. resuming a conversation)
onMounted(() => {
    if (chat.messages.length > 0) {
        scrollToBottom();
    }
    void refreshCampaignChats();
    scheduleCampaignChatRefresh();
});

// When intent changes while chat is already open, start a fresh conversation and pre-fill input
watch(
    () => props.context?.intent,
    (intent) => {
        if (!intent) return;
        chatId.value = generateUUID();
        chat.messages = [];
        chatLoadError.value = null;
        input.value = "";
        applyIntentPreFill(intent);
        nextTick(() => {
            promptInputRef.value?.focus();
        });
    },
);

const sendExample = async (example: string) => {
    input.value = example;
    await handleSubmit();
};

const messagesContainer = ref<HTMLElement>();
const promptInputRef = ref<{ focus: () => void; addFiles: (files: File[]) => Promise<void>; selectedFiles: ChatAttachedFile[] }>();
const fileDragDepth = ref(0);

function onFileDragEnter(event: DragEvent) {
    if (!event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    fileDragDepth.value++;
}

function onFileDragOver(event: DragEvent) {
    if (!event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = chatInputDisabled.value ? "none" : "copy";
}

function onFileDragLeave(event: DragEvent) {
    if (!event.dataTransfer?.types.includes("Files")) return;
    fileDragDepth.value = Math.max(0, fileDragDepth.value - 1);
}

async function onFileDrop(event: DragEvent) {
    fileDragDepth.value = 0;
    if (event.defaultPrevented || !event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    if (chatInputDisabled.value) return;
    await promptInputRef.value?.addFiles(Array.from(event.dataTransfer.files));
    promptInputRef.value?.focus();
}

// Part type for tool blocks
interface ToolPart {
    type: string;
    toolCallId?: string;
    state?: string;
    input?: unknown;
    output?: unknown;
    errorText?: string;
    [key: string]: unknown;
}

// Match only SDK argument-validation errors, not failures from executing the tool.
function isToolInputValidationError(part: ToolPart): boolean {
    return part.state === "output-error" && part.errorText?.startsWith(`Invalid input for tool ${part.type.slice(5)}:`) === true;
}

// Group consecutive parts by type (text/reasoning vs tool) to maintain natural order
interface PartGroup {
    type: "text" | "tool" | "narration";
    parts: Array<{ type: string; text?: string; [key: string]: unknown }>;
}

const groupParts = (parts: Array<{ type: string; text?: string; [key: string]: unknown }>): PartGroup[] => {
    const groups: PartGroup[] = [];
    let currentGroup: PartGroup | null = null;

    for (const part of parts) {
        const isTextPart = part.type === "text" || part.type === "reasoning" || part.type === "file";
        const isToolPart = part.type.startsWith("tool-");

        // Skip internal/streaming markers like 'step-start', 'step-finish', etc.
        if (!isTextPart && !isToolPart) continue;

        const groupType = isTextPart ? "text" : "tool";

        if (!currentGroup || currentGroup.type !== groupType) {
            currentGroup = { type: groupType, parts: [] };
            groups.push(currentGroup);
        }
        currentGroup.parts.push(part);
    }

    return groups;
};

/**
 * Collapse intertool prose without changing how reasoning, files, or tools render.
 * Keep trailing prose visible as it streams; collapse it only if another tool follows.
 */
const groupPartsCollapsed = (parts: Array<{ type: string; text?: string; [key: string]: unknown }>): PartGroup[] => {
    // Suggestions render below the answer, not as work that could collapse final prose.
    const groups = groupParts(parts.filter(part => part.type !== "tool-showSuggestedReplies"));
    const result: PartGroup[] = [];
    for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        const followsTool = groups[i - 1]?.type === "tool";
        const precedesTool = groups[i + 1]?.type === "tool";
        if (group.type === "text" && followsTool && precedesTool) {
            let currentGroup: PartGroup | null = null;
            for (const part of group.parts) {
                const type = part.type === "text" ? "narration" : "text";
                if (!currentGroup || currentGroup.type !== type) {
                    currentGroup = { type, parts: [] };
                    result.push(currentGroup);
                }
                currentGroup.parts.push(part);
            }
        } else {
            result.push(group);
        }
    }
    return result;
};

// SDK parts mutate in place. Remount when state changes so completion reaches the card.
const getPartKey = (part: ToolPart, index: number): string => {
    const hasOutput = part.output !== undefined;
    const state = part.state || "unknown";
    return `${index}-${part.toolCallId || ""}-${state}-${hasOutput}-${messageUpdateKey.value}`;
};

function isSuccessfulLaunchPart(part: ToolPart): boolean {
    const output = part.output as { launched?: boolean } | undefined;
    return part.state === "output-available" && output?.launched === true && !!icpId.value;
}

const postLaunchTargetRoute = ref<string>();
const showPostLaunchAction = ref(false);
const postLaunchActionLabel = computed(() => (postLaunchTargetRoute.value?.endsWith("/review") ? "Review contacts in launched campaign" : "Open launched campaign"));

async function openPostLaunchCampaign() {
    if (postLaunchTargetRoute.value) await navigateTo(postLaunchTargetRoute.value);
}

function isIterationLaunchPart(part: ToolPart): boolean {
    const output = part.output as { iterationCreated?: boolean } | undefined;
    return output?.iterationCreated === true;
}

function launchWorkSessionId(part: ToolPart): number | null {
    const output = part.output as { workSessionId?: number } | undefined;
    return typeof output?.workSessionId === "number" ? output.workSessionId : null;
}

// Dynamic message rendering — only render the most recent messages to keep the DOM light.
// Older messages load on demand when the user scrolls to the top.
const MESSAGES_PER_PAGE = 20;
const renderedMessageCount = ref(MESSAGES_PER_PAGE);

const visibleMessages = computed(() => {
    const all = chat.messages;
    const start = Math.max(0, all.length - renderedMessageCount.value);
    return all.slice(start);
});

const hasEarlierMessages = computed(() => chat.messages.length > renderedMessageCount.value);

const hiddenMessageCount = computed(() => Math.max(0, chat.messages.length - renderedMessageCount.value));

function loadEarlierMessages() {
    const el = messagesContainer.value;
    const prevHeight = el?.scrollHeight ?? 0;
    renderedMessageCount.value = Math.min(renderedMessageCount.value + MESSAGES_PER_PAGE, chat.messages.length);
    // Preserve scroll position so the viewport doesn't jump
    nextTick(() => {
        if (el) {
            const newHeight = el.scrollHeight;
            el.scrollTop += newHeight - prevHeight;
        }
    });
}

// When new messages arrive (e.g. streaming), keep the render window anchored to the tail
watch(
    () => chat.messages.length,
    (newLen, oldLen) => {
        if (newLen > oldLen) {
            renderedMessageCount.value = Math.min(renderedMessageCount.value + (newLen - oldLen), newLen);
        }
    },
);

// Auto-scroll to bottom when new content arrives, but only if already near the bottom
const isNearBottom = ref(true);

function onMessagesScroll() {
    const el = messagesContainer.value;
    if (!el) return;
    // Consider "near bottom" if within 80px of the bottom
    isNearBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
}

function scrollToBottom() {
    nextTick(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    });
}

watch(
    () => chat.messages.length,
    () => {
        // Scroll when a new message is added, but only if user hasn't scrolled up
        if (isNearBottom.value) {
            scrollToBottom();
        }
    },
);

watch(
    () => chat.messages.at(-1)?.parts,
    () => {
        // Scroll as content streams in, but only if user hasn't scrolled up
        if (isNearBottom.value) {
            scrollToBottom();
        }
    },
    { deep: true },
);

// Track tool outputs reactively during streaming so side effects (like icpId assignment and navigation)
// happen immediately when a tool finishes, not after the entire response stream completes.
const hasNavigatedForLaunch = ref(false);
watch(
    () => chat.messages.at(-1)?.parts,
    (parts) => {
        if (!parts || props.context?.page === "automatic-experiment") return;

        for (const p of parts) {
            if (!p.type.startsWith("tool-")) continue;
            const tp = p as unknown as { type: string; state?: string; output?: Record<string, unknown> };
            if (tp.state !== "output-available" || !tp.output) continue;

            if (tp.type === "tool-createCampaign" && tp.output.campaignId && !icpId.value) {
                icpId.value = tp.output.campaignId as number;
            }
            const targetIcpId = icpId.value;
            if (!targetIcpId) continue;
            if (
                shouldHandleCampaignLaunchOutput(
                    tp,
                    currentResponseActive.value,
                    hasNavigatedForLaunch.value,
                    targetIcpId,
                )
            ) {
                hasNavigatedForLaunch.value = true;
                refreshICP();
                if (refreshSequences) refreshSequences();
                refreshNuxtData(`icp-sequences-${targetIcpId}`);
                getPostLaunchNavigation(targetIcpId, route.path).then(({ targetRoute, shouldAutoNavigate }) => {
                    postLaunchTargetRoute.value = targetRoute;
                    if (!shouldAutoNavigate) {
                        showPostLaunchAction.value = true;
                        return;
                    }

                    // Hand off the Chat instance so the sidebar picks up the same SSE connection.
                    // This keeps the stream alive across navigation (no chat.stop() on unmount).
                    didHandoff.value = true;
                    chatHandoff.handoff(chat, chatId.value!, implementationDetailsMessageId, pendingMessage);
                    globalChat.switchChat(chatId.value!);
                    globalChat.chatContext.value = { page: getPostLaunchChatPage(targetRoute), icpId: targetIcpId };
                    globalChat.isOpen.value = true;
                    globalChat.isStreaming.value = true;
                    // Wait for the sidebar to mount and claim the Chat instance before navigating,
                    // otherwise navigation unmounts this component before the sidebar picks up the stream.
                    nextTick(() => {
                        navigateTo(targetRoute);
                    });
                });
            }
        }
    },
    { deep: true },
);
</script>

<style scoped>
/* Chat background — subtle warm gray tint for contrast with white message cards */
.chat-bg {
    background-color: #f8f8f7;
}

@keyframes bounce {
    0%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-6px);
    }
}

/* Collapsible blocks — chevron toggle + left line */
.collapsible-block > summary {
    list-style: none;
}
.collapsible-block > summary::-webkit-details-marker {
    display: none;
}
.collapsible-block > summary .collapsible-chevron {
    transition: transform 0.15s ease;
}
.collapsible-block[open] > summary .collapsible-chevron {
    transform: rotate(90deg);
}

/* Pulsing dot for active thinking indicator */
.thinking-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: rgb(59 130 246);
    animation: thinking-pulse 1.2s ease-in-out infinite;
}

@keyframes thinking-pulse {
    0%,
    100% {
        opacity: 0.4;
    }
    50% {
        opacity: 1;
    }
}

/* Markdown content styling — shared between main text and thinking blocks */
.chat-markdown {
    overflow-wrap: anywhere;
}

.chat-markdown :deep(> *) {
    margin: 0;
}

.chat-markdown :deep(> * + *) {
    margin-top: 0.75em;
}

.chat-markdown :deep(p) {
    line-height: 1.625;
}

/* Headings — darkest weight for hierarchy */
.chat-markdown :deep(h1) {
    margin-top: 1.25em;
    margin-bottom: 0.5em;
    font-size: 1.125rem;
    font-weight: 700;
    color: rgb(15 23 42);
}
.chat-markdown :deep(h2) {
    margin-top: 1em;
    margin-bottom: 0.4em;
    font-size: 1rem;
    font-weight: 700;
    color: rgb(15 23 42);
}
.chat-markdown :deep(h3) {
    margin-top: 0.75em;
    margin-bottom: 0.25em;
    font-size: 0.875rem;
    font-weight: 700;
    color: rgb(15 23 42);
}

/* Lists */
.chat-markdown :deep(ul) {
    list-style-type: disc;
    padding-left: 1.25rem;
}
.chat-markdown :deep(ol) {
    list-style-type: decimal;
    padding-left: 1.25rem;
}
.chat-markdown :deep(li) {
    margin-top: 0.25em;
    margin-bottom: 0.25em;
}

/* Code */
.chat-markdown :deep(code) {
    background: rgb(241 245 249);
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
}
.chat-markdown :deep(pre) {
    overflow-x: auto;
    max-width: 100%;
    background: rgb(241 245 249);
    padding: 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
}

/* Links */
.chat-markdown :deep(a) {
    color: rgb(37 99 235);
    text-decoration: underline;
    overflow-wrap: break-word;
}
.chat-markdown :deep(a:hover) {
    color: rgb(29 78 216);
}

/* Emphasis — bold text uses darker weight for contrast against body text */
.chat-markdown :deep(strong) {
    font-weight: 700;
    color: rgb(15 23 42);
}

/* Blockquotes — used for important summaries, keep full contrast */
.chat-markdown :deep(blockquote) {
    border-left: 3px solid rgb(59 130 246);
    padding-left: 0.75rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
}
.chat-markdown :deep(blockquote > * + *) {
    margin-top: 0.75em !important;
}

/* Tables */
.chat-markdown :deep(table) {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
}
.chat-markdown :deep(th) {
    text-align: left;
    font-weight: 600;
    padding: 0.375rem 0.5rem;
    border-bottom: 1.5px solid rgb(203 213 225);
    color: rgb(51 65 85);
}
.chat-markdown :deep(td) {
    padding: 0.3rem 0.5rem;
    border-bottom: 1px solid rgb(226 232 240);
    vertical-align: top;
}
.chat-markdown :deep(tr:last-child td) {
    border-bottom: none;
}
.chat-markdown :deep(thead) {
    border-bottom: 1.5px solid rgb(203 213 225);
}

/* Horizontal rules */
.chat-markdown :deep(hr) {
    border: none;
    border-top: 1px solid rgb(226 232 240);
    margin: 1em 0;
}
</style>
